<template>
    <div>
        <div v-if="isPlay" class="room-app" :class="{ 'is-compact': compactMode }">
            <navigation
                :show-chat="showChat"
                :compact-mode="compactMode"
                :show-cookie="isRoot || isAdmin"
                @toggle-compact="toggleCompactMode"
                @toggle-chat="toggleChat"
                @search="openSearch = true; activeNav = 'search'"
                @share="shareRoom"
                @profile="openProfileDialog"
                @cookie="openCookie = true"
            ></navigation>
            <div class="room-body" :class="{ 'is-chat-hidden': !showChat }">
                <aside class="room-sidenav">
                    <button
                        type="button"
                        class="room-nav-item"
                        :class="{ 'is-active': activeNav === 'playing' }"
                        @click="selectNav('playing')">
                        <mu-icon value="album" :size="20"></mu-icon>
                        <span class="room-nav-label">正在播放</span>
                        <span class="room-nav-dot" v-if="activeNav === 'playing'"></span>
                    </button>
                    <button
                        type="button"
                        class="room-nav-item"
                        :class="{ 'is-active': activeNav === 'search' }"
                        @click="selectNav('search')">
                        <mu-icon value="search" :size="20"></mu-icon>
                        <span class="room-nav-label">搜索点歌</span>
                    </button>
                    <button
                        type="button"
                        class="room-nav-item"
                        :class="{ 'is-active': activeNav === 'playlist' }"
                        @click="selectNav('playlist')">
                        <mu-icon value="library_music" :size="20"></mu-icon>
                        <span class="room-nav-label">平台歌单</span>
                    </button>
                    <button
                        type="button"
                        class="room-nav-item"
                        :class="{ 'is-active': activeNav === 'favorite' }"
                        @click="selectNav('favorite')">
                        <mu-icon value="favorite_border" :size="20"></mu-icon>
                        <span class="room-nav-label">我的收藏</span>
                    </button>
                    <button
                        type="button"
                        class="room-nav-item"
                        :class="{ 'is-active': activeNav === 'history' }"
                        @click="selectNav('history')">
                        <mu-icon value="history" :size="20"></mu-icon>
                        <span class="room-nav-label">点歌历史</span>
                    </button>
                </aside>

                <main class="room-main">
                    <section class="room-nowplaying">
                        <div class="room-cover-wrap">
                            <div class="room-cover" :class="{ 'is-rotating': albumRotate }">
                                <img :src="coverSrc" alt="cover">
                            </div>
                        </div>
                        <div class="room-track">
                            <div class="room-now-tags">
                                <span class="room-tag is-now">NOW PLAYING</span>
                            </div>
                            <div class="room-track-title">{{ music && music.name ? music.name : '暂无播放' }}</div>
                            <div class="room-track-meta">
                                {{ music && music.artist ? music.artist : '未知歌手' }}
                                <span v-if="music && music.album && music.album.name"> · {{ music.album.name }}</span>
                            </div>
                            <div class="room-lyric-current">
                                <div class="room-lyric-current-lines">
                                    <div class="room-lyric-current-now">{{ (focusCurrentLyric && focusCurrentLyric.text) || lyric || '暂无歌词' }}</div>
                                    <div class="room-lyric-current-next">{{ (focusNextLyric && focusNextLyric.text) || '' }}</div>
                                </div>
                            </div>
                            <div class="room-progress">
                                <div class="room-progress-bar">
                                    <div class="room-progress-track">
                                        <div class="room-progress-fill" :style="{ width: (progress || 0) + '%' }"></div>
                                    </div>
                                    <div class="room-progress-thumb" :style="{ left: (progress || 0) + '%' }"></div>
                                </div>
                                <div class="room-progress-times">
                                    <span>{{ progressUsedTime }}</span>
                                    <span id="musicEndTime">{{ progressTotalTime }}</span>
                                </div>
                            </div>
                            <div class="room-controls">
                                <div class="room-controls-left">
                                    <button
                                        type="button"
                                        class="room-ctrl-btn"
                                        :class="{ 'is-fav': isCurrentFavorite }"
                                        :title="isCurrentFavorite ? '取消收藏' : '收藏'"
                                        @click="toggleFavoriteCurrent">
                                        <mu-icon :value="isCurrentFavorite ? 'favorite' : 'favorite_border'" :size="22"></mu-icon>
                                    </button>
                                    <button type="button" class="room-ctrl-btn" title="投票切歌" @click="musicSkipVote">
                                        <mu-icon value="skip_next" :size="28"></mu-icon>
                                    </button>
                                    <button type="button" class="room-play-btn" :title="isAudioPlaying ? '暂停' : '播放'" @click="toggleAudioPlay">
                                        <mu-icon :value="isAudioPlaying ? 'pause' : 'play_arrow'" :size="28" color="#fff"></mu-icon>
                                    </button>
                                    <mu-button class="room-pick-btn" color="#e74c3c" @click="openSearch = true; activeNav = 'search'">点歌</mu-button>
                                </div>
                                <div class="room-volume">
                                    <mu-icon :value="volume > 0 ? 'volume_up' : 'volume_off'" :size="20"></mu-icon>
                                    <mu-slider class="demo-slider" color="#e74c3c" v-model="volume"></mu-slider>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section class="room-list-panel">
                        <div class="room-tabs">
                            <button type="button" class="room-tab" :class="{ 'is-active': mainTab === 'queue' }" @click="selectMainTab('queue')">待播队列</button>
                            <button type="button" class="room-tab" :class="{ 'is-active': mainTab === 'lyrics' }" @click="selectMainTab('lyrics')">歌词</button>
                            <button type="button" class="room-tab" :class="{ 'is-active': mainTab === 'favorite' }" @click="selectMainTab('favorite')">收藏</button>
                            <button type="button" class="room-tab" :class="{ 'is-active': mainTab === 'hot' }" @click="selectMainTab('hot')">热歌榜</button>
                        </div>
                        <div class="room-tab-body" ref="tabBody">
                            <div v-show="mainTab === 'queue'" class="room-queue-pane">
                                <div class="room-queue-head" v-if="pick && pick.length">
                                    <span></span>
                                    <span>序号</span>
                                    <span>歌曲名</span>
                                    <span>歌手</span>
                                    <span>点歌人</span>
                                    <span>点赞</span>
                                    <span></span>
                                </div>
                                <div
                                    v-if="pick && pick.length"
                                    ref="queueList"
                                    class="room-queue-list"
                                    @scroll="onQueueListScroll"
                                    @wheel.stop>
                                    <div
                                        class="room-queue-row"
                                        v-for="(row, index) in pick"
                                        :key="row.id || index"
                                        :class="{ 'is-current': isCurrentPick(row, index) }">
                                        <button
                                            type="button"
                                            class="room-fav-btn"
                                            :class="{ 'is-on': isSongFavorite(row) }"
                                            :title="isSongFavorite(row) ? '取消收藏' : '收藏'"
                                            @click.stop="toggleFavoriteSong(row)">
                                            <mu-icon :value="isSongFavorite(row) ? 'favorite' : 'favorite_border'" :size="18"></mu-icon>
                                        </button>
                                        <span class="room-queue-index">{{ formatQueueIndex(index) }}</span>
                                        <span class="room-queue-name-cell">
                                            <span class="room-queue-name">{{ isRoot || isAdmin ? row.name + '[' + row.id + ']' : row.name }}</span>
                                            <button
                                                type="button"
                                                class="room-queue-copy"
                                                title="复制歌名"
                                                @click.stop="copyQueueSongName(row)">
                                                <mu-icon value="content_copy" :size="14"></mu-icon>
                                            </button>
                                        </span>
                                        <span class="room-queue-artist">{{ row.artist }}</span>
                                        <span class="room-queue-user">{{ formatQueueUser(row) }}</span>
                                        <span
                                            v-if="index !== 0"
                                            class="room-queue-like"
                                            :class="{
                                                'is-liked': isLikedByMe(row),
                                                'has-votes': goodCount(row) > 0
                                            }"
                                            :title="isLikedByMe(row) ? ('取消点赞 · 当前 ' + goodCount(row) + ' 票') : ('点赞置顶 · 当前 ' + goodCount(row) + ' 票')"
                                            @click.stop="goodMusic(row)">
                                            <span class="music-good-icon" aria-hidden="true">👍</span>
                                            <span v-if="goodCount(row) > 0" class="music-good-count">{{ goodCount(row) }}</span>
                                        </span>
                                        <span v-else class="room-queue-like is-playing-tag">播放中</span>
                                        <button
                                            v-if="canRemovePick(row, index)"
                                            type="button"
                                            class="room-queue-del"
                                            title="从待播移除"
                                            @click.stop="removePickMusic(row, index)">
                                            <mu-icon value="close" :size="16"></mu-icon>
                                        </button>
                                        <span v-else class="room-queue-del-placeholder"></span>
                                    </div>
                                </div>
                                <div v-else class="room-empty">
                                    <div class="room-empty-title">暂无待播</div>
                                    <div class="room-empty-desc">点一首歌，房间就会热闹起来</div>
                                </div>
                            </div>
                            <div v-show="mainTab === 'lyrics'" class="room-lyric-wrap" @wheel.stop>
                                <div class="room-lyric-list" v-if="lyricLines && lyricLines.length">
                                    <div
                                        class="room-lyric-line"
                                        v-for="line in lyricLines"
                                        :key="line.index"
                                        :class="{ 'is-active': line.index === activeLyricIndex }">
                                        {{ line.text }}
                                    </div>
                                </div>
                                <div v-else class="room-empty">
                                    <div class="room-empty-title">暂无歌词</div>
                                    <div class="room-empty-desc">当前歌曲还没有可展示的歌词</div>
                                </div>
                            </div>
                            <div v-show="mainTab === 'favorite'" class="room-tab-pane room-inline-pane" @wheel.stop>
                                <div class="room-inline-toolbar">
                                    <button type="button" class="discover-chip is-solid" @click="playAllFavorites">播放全部</button>
                                    <button type="button" class="discover-chip" @click="exportFavorites">导出</button>
                                    <button type="button" class="discover-chip" @click="openFavoriteImport = true">导入</button>
                                    <button type="button" class="discover-chip" @click="clearFavoriteList">清空</button>
                                </div>
                                <div class="room-inline-list" v-if="favoriteList.length">
                                    <div
                                        class="discover-row"
                                        v-for="(row, index) in favoriteList"
                                        :key="row.id || index">
                                        <div class="discover-cover">
                                            <img :src="searchCover(row)" alt="" @error="onCoverError">
                                        </div>
                                        <div class="discover-info">
                                            <div class="discover-name">{{ row.name }}</div>
                                            <div class="discover-meta">
                                                {{ row.artist || '未知歌手' }}
                                                <template v-if="searchAlbumName(row)"> · {{ searchAlbumName(row) }}</template>
                                            </div>
                                        </div>
                                        <div class="discover-actions">
                                            <button
                                                type="button"
                                                class="room-fav-btn is-on"
                                                title="取消收藏"
                                                @click.stop="toggleFavoriteSong(row)">
                                                <mu-icon value="favorite" :size="18"></mu-icon>
                                            </button>
                                            <button type="button" class="discover-pick-btn" @click.stop="tryPickMusic(row)">点歌</button>
                                        </div>
                                    </div>
                                </div>
                                <div v-else class="room-empty">
                                    <div class="room-empty-title">暂无收藏歌曲</div>
                                    <div class="room-empty-desc">点播放器或列表里的心形，就能把歌收进来</div>
                                </div>
                            </div>
                            <div v-show="mainTab === 'hot'" class="room-tab-pane room-inline-pane" @wheel.stop>
                                <div class="room-inline-list" v-if="hotList && hotList.length">
                                    <div
                                        class="discover-row"
                                        v-for="(row, index) in hotList"
                                        :key="listRowKey(row, index)">
                                        <div class="discover-cover">
                                            <img :src="searchCover(row)" alt="" @error="onCoverError">
                                        </div>
                                        <div class="discover-info">
                                            <div class="discover-name">{{ row.name }}</div>
                                            <div class="discover-meta">
                                                {{ row.artist || '未知歌手' }}
                                                <template v-if="searchAlbumName(row)"> · {{ searchAlbumName(row) }}</template>
                                            </div>
                                        </div>
                                        <div class="discover-actions">
                                            <button
                                                type="button"
                                                class="room-fav-btn"
                                                :class="{ 'is-on': isSongFavorite(row) }"
                                                :title="isSongFavorite(row) ? '取消收藏' : '收藏'"
                                                @click.stop="toggleFavoriteSong(row)">
                                                <mu-icon :value="isSongFavorite(row) ? 'favorite' : 'favorite_border'" :size="18"></mu-icon>
                                            </button>
                                            <button type="button" class="discover-pick-btn" @click.stop="tryPickMusic(row)">点歌</button>
                                        </div>
                                    </div>
                                    <infinite-footer
                                        :loading="hotLoading"
                                        :done="!hotHasMore"
                                        @load="loadMoreHot"></infinite-footer>
                                </div>
                                <div v-else class="room-empty">
                                    <div class="room-empty-title">{{ hotLoading ? '加载中…' : '暂无热歌数据' }}</div>
                                    <div class="room-empty-desc">{{ hotLoading ? '正在读取 QQ 热歌榜' : (hotError || '稍后再试，或用搜索点歌') }}</div>
                                </div>
                            </div>
                        </div>
                    </section>
                </main>

                <div class="room-chat-mask" v-if="showChat && screenWidth > 0 && screenWidth <= 1100" @click="showChat = false"></div>
                <aside class="room-chat" v-show="showChat">
                    <div class="room-chat-header">
                        <div>
                            <span class="room-chat-title">房间聊天</span>
                            <span class="room-chat-online" :class="{ 'is-on': isContented }">{{ online || 0 }} 人在线</span>
                        </div>
                        <div class="room-chat-actions">
                            <button
                                type="button"
                                class="room-icon-btn"
                                :class="{ 'is-on': openOnlineMembers }"
                                title="在线成员"
                                @click="showOnlineMembers">
                                <mu-icon value="people_outline" :size="20"></mu-icon>
                            </button>
                            <button type="button" class="room-icon-btn" title="清空聊天显示" @click="clearLocalChat">
                                <mu-icon value="delete_sweep" :size="20"></mu-icon>
                            </button>
                            <button type="button" class="room-icon-btn" title="关闭聊天" @click="showChat = false">
                                <mu-icon value="close" :size="20"></mu-icon>
                            </button>
                        </div>
                    </div>
                    <div id="chat-container" class="room-chat-list" @scroll="onChatListScroll" @wheel.stop>
                        <div
                            v-for="(item, index) in chatData"
                            :key="index"
                            class="room-chat-item"
                            :class="{ 'is-notice': item.type === 'notice' }">
                            <div v-if="item.type !== 'notice'">
                                <small class="chat-data-user">{{ formatChatUser(item) }}</small>
                            </div>
                            <div v-if="item.type === 'notice'">
                                <span class="chat-data-notice">{{ item.content }}</span>
                            </div>
                            <div v-if="item.type === 'chat'" class="chat-data-content">
                                <span>{{ item.content }}</span>
                                <template v-if="item.images && item.images.length">
                                    <img v-for="(img, imgIndex) in item.images" :key="imgIndex" :src="img" alt="" style="width: 100%; display: block; margin-top: 6px; border-radius: 8px;">
                                </template>
                            </div>
                        </div>
                    </div>
                    <div class="room-reaction-wrap" ref="reactionWrap">
                        <div class="room-reaction-stage" aria-hidden="true">
                            <span
                                v-for="floater in reactionFloats"
                                :key="floater.id"
                                class="room-reaction-float"
                                :style="{
                                    left: floater.left + '%',
                                    '--dx': floater.dx + 'px',
                                    '--dy': floater.dy + 'px'
                                }"
                                @animationend="onReactionFloatEnd(floater.id, $event)">{{ floater.emoji }}</span>
                        </div>
                        <div class="room-reaction-bar" :class="{ 'is-locked': reactionLocked }">
                            <button
                                type="button"
                                class="room-reaction-btn"
                                v-for="(emoji, rIndex) in reactionEmojis"
                                :key="emoji + rIndex"
                                :title="emoji"
                                :disabled="reactionLocked"
                                @click="sendReaction(emoji, $event)">
                                <span class="room-reaction-glyph">{{ emoji }}</span>
                                <span
                                    v-if="reactionBurst.show && reactionBurst.emoji === emoji"
                                    class="room-reaction-burst">{{ emoji }}×{{ reactionBurst.count }}</span>
                            </button>
                        </div>
                    </div>
                    <div class="room-chat-composer" ref="chatComposer">
                        <mu-text-field
                            ref="chatField"
                            :value="chatMessage"
                            @input="updateChatMessage"
                            @keydown.native.enter.prevent="sendHandler"
                            @click.native="rememberChatCaret"
                            @keyup.native="rememberChatCaret"
                            @mouseup.native="rememberChatCaret"
                            placeholder="说点什么，Enter 发送"
                            color="primary"
                            class="width-size-100 chat-message"></mu-text-field>
                        <div class="room-chat-send-row">
                            <div class="room-emoji-wrap" ref="emojiWrap">
                                <button
                                    type="button"
                                    class="room-emoji-btn"
                                    :class="{ 'is-on': showEmojiPanel }"
                                    title="表情"
                                    @mousedown.prevent
                                    @click.stop="toggleEmojiPanel">
                                    <mu-icon value="tag_faces" :size="24"></mu-icon>
                                </button>
                                <div v-show="showEmojiPanel" class="room-emoji-panel" @mousedown.prevent>
                                    <button
                                        type="button"
                                        class="room-emoji-cell"
                                        v-for="(emo, emoIndex) in chatEmojiList"
                                        :key="emoIndex"
                                        @click.stop="insertChatEmoji(emo)">{{ emo }}</button>
                                </div>
                            </div>
                            <mu-button v-if="!isContented" @click="connect" class="room-send-btn" color="#e74c3c">
                                连接服务器
                            </mu-button>
                            <mu-button v-else @click="sendHandler" class="room-send-btn" color="#e74c3c">
                                发送
                            </mu-button>
                        </div>
                    </div>
                </aside>
            </div>
            <div>
                <audio id="music" :src="audioPlaybackSrc" @timeupdate="musicTimeUpdate" controls="" autoplay="autoplay"  @canplaythrough="nextSong"
                       @stalled="onAudioStuck('stalled')"
                       @waiting="onAudioStuck('waiting')"
                       @error="onAudioMediaError"
                       @pause="onAudioPause"
                       @playing="onAudioPlaying"
                       @ended="onAudioEnded"
                       style="display: none"></audio>
		 <audio id="music2" :src="music2 && music2.url ? music2.url : ''" style="display: none"></audio>
            </div>
            <div v-show="compactMode" class="focus-mode">
                <div class="focus-bg" :style="{ backgroundImage: 'url(' + coverSrc + ')' }"></div>
                <div class="focus-shade"></div>
                <div class="focus-brand">简洁模式</div>
                <button type="button" class="focus-exit" title="退出简洁模式" @click="toggleCompactMode">
                    <mu-icon value="fullscreen_exit" :size="20"></mu-icon>
                    <span>退出简洁模式</span>
                </button>
                <div class="focus-split">
                    <div class="focus-left">
                        <div class="focus-cover" :class="{ 'is-rotating': albumRotate }">
                            <img :src="coverSrc" alt="cover">
                        </div>
                    </div>
                    <div class="focus-right">
                        <div class="focus-copy">
                            <div class="focus-tag">NOW PLAYING</div>
                            <div class="focus-title">{{ music && music.name ? music.name : '暂无播放' }}</div>
                            <div class="focus-artist">
                                {{ music && music.artist ? music.artist : '未知歌手' }}
                                <span v-if="music && music.album && music.album.name"> · {{ music.album.name }}</span>
                            </div>
                        </div>
                        <div class="focus-lyrics">
                            <div class="focus-lyric-line is-prev">{{ focusPrevLyric ? focusPrevLyric.text : '' }}</div>
                            <div class="focus-lyric-line is-active">
                                <span class="focus-lyric-text">{{ focusCurrentLyric ? focusCurrentLyric.text : '暂无歌词' }}</span>
                            </div>
                            <div class="focus-lyric-line is-next">{{ focusNextLyric ? focusNextLyric.text : '' }}</div>
                        </div>
                        <div class="focus-dock">
                            <div class="focus-progress">
                                <div class="focus-progress-track">
                                    <div class="focus-progress-fill" :style="{ width: (progress || 0) + '%' }"></div>
                                </div>
                                <span class="focus-progress-time">{{ playerTime }}</span>
                            </div>
                            <div class="focus-controls">
                                <button
                                    type="button"
                                    class="focus-icon-btn"
                                    :class="{ 'is-on': isCurrentFavorite }"
                                    :title="isCurrentFavorite ? '取消收藏' : '收藏'"
                                    @click="toggleFavoriteCurrent">
                                    <mu-icon :value="isCurrentFavorite ? 'favorite' : 'favorite_border'" :size="22"></mu-icon>
                                </button>
                                <button type="button" class="focus-pick-btn" title="投票切歌" @click="musicSkipVote">切歌</button>
                                <button type="button" class="focus-play-btn" :title="isAudioPlaying ? '暂停' : '播放'" @click="toggleAudioPlay">
                                    <mu-icon :value="isAudioPlaying ? 'pause' : 'play_arrow'" :size="34" color="#fff"></mu-icon>
                                </button>
                                <button type="button" class="focus-pick-btn" @click="openSearch = true; activeNav = 'search'">点歌</button>
                                <div class="focus-volume">
                                    <mu-icon :value="volume > 0 ? 'volume_up' : 'volume_off'" :size="20"></mu-icon>
                                    <mu-slider class="demo-slider" color="#e74c3c" v-model="volume"></mu-slider>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div id="play" class="room-enter" v-if="!isPlay">
            <div class="room-enter-group">
                <button type="button" class="room-enter-play" @click="play" title="点击播放进入">
                    <mu-icon value="play_arrow" :size="72" color="#fff"></mu-icon>
                </button>
                <div class="room-enter-hint">点击播放进入房间</div>
            </div>
        </div>
        <mu-dialog id="history-dialog" class="discover-dialog history-dialog" width="90%" max-width="760" :open.sync="openHistory">
            <history-panel @close="openHistory = false"></history-panel>
        </mu-dialog>
        <mu-dialog id="profile-dialog" class="discover-dialog profile-dialog" width="90%" max-width="520" :open.sync="openProfile">
            <div class="discover-panel profile-panel">
                <div class="discover-head">
                    <div>
                        <div class="discover-kicker">PROFILE</div>
                        <div class="discover-title">个人设置</div>
                        <div class="discover-sub">修改你在听歌房显示的昵称</div>
                    </div>
                    <button type="button" class="discover-close" title="关闭" @click="openProfile = false">
                        <mu-icon value="close" :size="20"></mu-icon>
                    </button>
                </div>
                <div class="profile-block">
                    <div class="profile-block-title">个人昵称</div>
                    <div class="profile-block-desc">设置你在听歌房和聊天区显示的名称</div>
                    <input
                        class="discover-input"
                        v-model="profileNameDraft"
                        maxlength="20"
                        placeholder="输入你的昵称"
                        @keydown.enter.prevent="saveProfileName"
                    >
                    <button type="button" class="discover-search-btn profile-action-btn" @click="saveProfileName">保存昵称</button>
                </div>
                <div class="profile-block" v-if="isRoot || isAdmin">
                    <div class="profile-block-title">Cookie 管理</div>
                    <div class="profile-block-desc">管理员可粘贴更新 QQ 播放 Cookie</div>
                    <button type="button" class="discover-search-btn profile-action-btn" @click="openCookie = true; openProfile = false">打开 Cookie 管理</button>
                </div>
            </div>
        </mu-dialog>
        <mu-dialog id="online-dialog" class="discover-dialog online-dialog" width="90%" max-width="640" :open.sync="openOnlineMembers">
            <div class="discover-panel online-panel">
                <div class="discover-head">
                    <div>
                        <div class="discover-kicker">ONLINE</div>
                        <div class="discover-title">在线成员 · {{ online || 0 }}</div>
                        <div class="discover-sub">当前 {{ online || 0 }} 人在线</div>
                    </div>
                    <button type="button" class="discover-close" title="关闭" @click="openOnlineMembers = false">
                        <mu-icon value="close" :size="20"></mu-icon>
                    </button>
                </div>
                <div class="discover-filter-row">
                    <button type="button" class="discover-chip" @click="refreshOnlineMembers">刷新成员</button>
                </div>
                <div class="discover-stats">
                    <span>当前 <b>{{ online || 0 }}</b> 人在线</span>
                    <span v-if="onlineMemberList.length">已列出 {{ onlineMemberList.length }} 人</span>
                </div>
                <div class="online-list" v-if="onlineMemberList.length">
                    <div
                        class="online-row"
                        v-for="(member, index) in onlineMemberList"
                        :key="member.sessionId || member.maskedIp || index">
                        <div class="online-avatar" :style="memberAvatarStyle(member.sessionId || member.nickName || member.maskedIp)">
                            <span>{{ memberAvatar(member.sessionId || member.nickName || member.maskedIp) }}</span>
                        </div>
                        <div class="online-info">
                            <div class="online-name">{{ member.displayName }}</div>
                        </div>
                    </div>
                </div>
                <div class="discover-empty" v-else>
                    <div class="discover-empty-title">暂无成员详情</div>
                </div>
            </div>
        </mu-dialog>
        <mu-dialog id="cookie-dialog" class="cookie-dialog" width="90%" max-width="560" :open.sync="openCookie">
            <div class="cookie-admin">
                <div class="cookie-admin-title">更新 QQ 播放 Cookie（管理员）</div>
                <div class="cookie-status-line" :class="{ 'is-ok': cookieStatusOk, 'is-bad': cookieStatusOk === false }">
                    {{ cookieStatusText }}
                </div>
                <p class="cookie-admin-tip">从 y.qq.com 复制完整 Cookie（需含 uin、qm_keyst）后保存</p>
                <mu-text-field multi-line :rows="4" v-model="cookieInput" placeholder="粘贴完整 Cookie 字符串..." full-width></mu-text-field>
                <mu-button color="primary" style="width:100%;margin-top:8px" @click="submitCookie" :disabled="cookieSubmitting">
                    {{ cookieSubmitting ? '提交中…' : '保存 Cookie' }}
                </mu-button>
                <p class="cookie-admin-tip" style="margin-top:12px">命令方式：<code>设置Cookie 完整Cookie字符串</code></p>
            </div>
        </mu-dialog>
        <mu-dialog id="share-fallback-dialog" class="share-fallback-dialog" width="90%" max-width="420" :open.sync="openShareFallback">
            <div class="share-fallback">
                <div class="share-fallback-title">复制失败，请长按下方文案手动复制</div>
                <mu-text-field multi-line :rows="4" :value="shareFallbackText" readonly full-width
                               class="share-fallback-input"></mu-text-field>
                <mu-button color="primary" style="width:100%;margin-top:8px" @click="retryCopyShare">
                    再试一次复制
                </mu-button>
                <mu-button flat color="primary" style="width:100%;margin-top:4px" @click="openShareFallback=false">
                    关闭
                </mu-button>
            </div>
        </mu-dialog>
        <mu-dialog id="search" class="discover-dialog search-music-dialog" width="90%" max-width="760" :open.sync="openSearch">
            <div class="discover-panel">
                <div class="discover-head">
                    <div>
                        <div class="discover-kicker">DISCOVER</div>
                        <div class="discover-title">搜索点歌</div>
                        <div class="discover-sub">搜索歌曲并点播</div>
                    </div>
                    <button type="button" class="discover-close" title="关闭" @click="openSearch = false">
                        <mu-icon value="close" :size="20"></mu-icon>
                    </button>
                </div>
                <div class="discover-toolbar">
                    <span class="discover-toolbar-label">歌曲搜索</span>
                    <div class="discover-toolbar-actions">
                        <button type="button" class="discover-chip is-solid" @click="openHotChart">热歌榜</button>
                        <button type="button" class="discover-chip" @click="openPlaylistDialog">前往平台歌单</button>
                    </div>
                </div>
                <div class="discover-search-row">
                    <input
                        class="discover-input"
                        :value="searchKeyword"
                        @input="updateSearchKeyword($event.target.value)"
                        @keydown.enter.prevent="search"
                        placeholder="搜索歌曲、歌手或专辑"
                    >
                    <button type="button" class="discover-search-btn" @click="search">搜索</button>
                </div>
                <div class="discover-stats">
                    <span>已显示 <b>{{ searchShownCount }}</b> 首歌曲</span>
                    <span>共 {{ searchCount || 0 }} 条</span>
                </div>
                <div class="discover-list" v-if="searchData && searchData.length">
                    <div
                        class="discover-row"
                        v-for="(row, index) in searchData"
                        :key="listRowKey(row, index)">
                        <div class="discover-cover">
                            <img :src="searchCover(row)" alt="" @error="onCoverError">
                        </div>
                        <div class="discover-info">
                            <div class="discover-name">{{ row.name }}</div>
                            <div class="discover-meta">
                                {{ row.artist || '未知歌手' }}
                                <template v-if="searchAlbumName(row)"> · {{ searchAlbumName(row) }}</template>
                            </div>
                        </div>
                        <div class="discover-actions">
                            <button
                                type="button"
                                class="room-fav-btn"
                                :class="{ 'is-on': isSongFavorite(row) }"
                                :title="isSongFavorite(row) ? '取消收藏' : '收藏'"
                                @click.stop="toggleFavoriteSong(row)">
                                <mu-icon :value="isSongFavorite(row) ? 'favorite' : 'favorite_border'" :size="18"></mu-icon>
                            </button>
                            <button type="button" class="discover-pick-btn" @click.stop="tryPickMusic(row)">点歌</button>
                        </div>
                    </div>
                    <infinite-footer
                        :loading="searchLoading"
                        :done="!searchHasMore"
                        @load="loadMoreSearch"></infinite-footer>
                </div>
                <div class="discover-empty" v-else>
                    <div class="discover-empty-title">{{ searchLoading ? '加载中…' : (searchKeyword ? '没有找到相关歌曲' : '搜索歌曲并点播') }}</div>
                    <div class="discover-empty-desc">{{ searchLoading ? '正在搜索歌曲' : '输入歌名或歌手，回车或点击搜索' }}</div>
                </div>
            </div>
        </mu-dialog>
        <mu-dialog id="hot-chart" class="discover-dialog hot-chart-dialog" width="90%" max-width="760" :open.sync="openHot">
            <div class="discover-panel">
                <div class="discover-head">
                    <div>
                        <div class="discover-kicker">TRENDING</div>
                        <div class="discover-title">热歌榜</div>
                        <div class="discover-sub">读取最新热歌榜（QQ）</div>
                    </div>
                    <button type="button" class="discover-close" title="关闭" @click="openHot = false">
                        <mu-icon value="close" :size="20"></mu-icon>
                    </button>
                </div>
                <div class="discover-stats">
                    <span>已显示 <b>{{ hotShownCount }}</b> 首歌曲</span>
                    <span>共 {{ hotCount || 0 }} 条</span>
                </div>
                <div class="discover-list" v-if="hotList && hotList.length">
                    <div
                        class="discover-row"
                        v-for="(row, index) in hotList"
                        :key="listRowKey(row, index)">
                        <div class="discover-cover">
                            <img :src="searchCover(row)" alt="" @error="onCoverError">
                        </div>
                        <div class="discover-info">
                            <div class="discover-name">{{ row.name }}</div>
                            <div class="discover-meta">
                                {{ row.artist || '未知歌手' }}
                                <template v-if="searchAlbumName(row)"> · {{ searchAlbumName(row) }}</template>
                            </div>
                        </div>
                        <div class="discover-actions">
                            <button
                                type="button"
                                class="room-fav-btn"
                                :class="{ 'is-on': isSongFavorite(row) }"
                                :title="isSongFavorite(row) ? '取消收藏' : '收藏'"
                                @click.stop="toggleFavoriteSong(row)">
                                <mu-icon :value="isSongFavorite(row) ? 'favorite' : 'favorite_border'" :size="18"></mu-icon>
                            </button>
                            <button type="button" class="discover-pick-btn" @click.stop="tryPickMusic(row)">点歌</button>
                        </div>
                    </div>
                    <infinite-footer
                        :loading="hotLoading"
                        :done="!hotHasMore"
                        @load="loadMoreHot"></infinite-footer>
                </div>
                <div class="discover-empty" v-else>
                    <div class="discover-empty-title">{{ hotLoading ? '加载中…' : '暂无热歌数据' }}</div>
                    <div class="discover-empty-desc">{{ hotLoading ? '正在读取 QQ 热歌榜' : (hotError || '稍后再试，或用搜索点歌') }}</div>
                </div>
            </div>
        </mu-dialog>
        <mu-dialog id="favorite-dialog" class="discover-dialog favorite-dialog" width="90%" max-width="760" :open.sync="openFavorite">
            <div class="discover-panel">
                <div class="discover-head">
                    <div>
                        <div class="discover-kicker">COLLECTION</div>
                        <div class="discover-title">我的收藏</div>
                        <div class="discover-sub">保存在当前浏览器，刷新不丢失</div>
                    </div>
                    <button type="button" class="discover-close" title="关闭" @click="openFavorite = false">
                        <mu-icon value="close" :size="20"></mu-icon>
                    </button>
                </div>
                <div class="discover-filter-row">
                    <button type="button" class="discover-chip is-solid" @click="playAllFavorites">播放全部</button>
                    <button type="button" class="discover-chip" @click="exportFavorites">导出</button>
                    <button type="button" class="discover-chip" @click="openFavoriteImport = true">导入</button>
                    <button type="button" class="discover-chip" @click="clearFavoriteList">清空</button>
                </div>
                <div class="discover-stats">
                    <span>已显示 <b>{{ favoriteShownCount }}</b> 首歌曲</span>
                    <span>共 {{ favoriteList.length }} 条</span>
                </div>
                <div class="discover-list" v-if="favoriteList.length">
                    <div
                        class="discover-row"
                        v-for="(row, index) in favoriteList"
                        :key="row.id || index">
                        <div class="discover-cover">
                            <img :src="searchCover(row)" alt="" @error="onCoverError">
                        </div>
                        <div class="discover-info">
                            <div class="discover-name">{{ row.name }}</div>
                            <div class="discover-meta">
                                {{ row.artist || '未知歌手' }}
                                <template v-if="searchAlbumName(row)"> · {{ searchAlbumName(row) }}</template>
                            </div>
                        </div>
                        <div class="discover-actions">
                            <button
                                type="button"
                                class="room-fav-btn is-on"
                                title="取消收藏"
                                @click.stop="toggleFavoriteSong(row)">
                                <mu-icon value="favorite" :size="18"></mu-icon>
                            </button>
                            <button type="button" class="discover-pick-btn" @click.stop="tryPickMusic(row)">点歌</button>
                        </div>
                    </div>
                </div>
                <div class="discover-empty" v-else>
                    <div class="discover-empty-title">暂无收藏歌曲</div>
                    <div class="discover-empty-desc">点播放器或列表里的心形，就能把歌收进来</div>
                </div>
            </div>
        </mu-dialog>
        <mu-dialog id="favorite-import-dialog" class="discover-dialog" width="90%" max-width="520" :open.sync="openFavoriteImport">
            <div class="discover-panel">
                <div class="discover-head">
                    <div>
                        <div class="discover-kicker">IMPORT</div>
                        <div class="discover-title">导入收藏</div>
                        <div class="discover-sub">粘贴 JSON，或选择之前导出的文件</div>
                    </div>
                    <button type="button" class="discover-close" title="关闭" @click="openFavoriteImport = false">
                        <mu-icon value="close" :size="20"></mu-icon>
                    </button>
                </div>
                <textarea
                    class="favorite-import-input"
                    v-model="favoriteImportText"
                    placeholder='[{"id":"...","name":"...","artist":"..."}]'></textarea>
                <div class="discover-filter-row">
                    <button type="button" class="discover-chip" @click="pickFavoriteFile">选择文件</button>
                    <button type="button" class="discover-chip is-solid" @click="importFavoritesFromText">导入</button>
                </div>
                <input ref="favoriteFile" type="file" accept="application/json,.json,text/plain" style="display:none" @change="onFavoriteFileChange">
            </div>
        </mu-dialog>
        <mu-dialog id="playlist-dialog" class="discover-dialog playlist-dialog" width="90%" max-width="760" :open.sync="openPlaylist">
            <div class="discover-panel">
                <div class="discover-head">
                    <div>
                        <div class="discover-kicker">PLAYLISTS</div>
                        <div class="discover-title">{{ playlistView === 'detail' && playlistDetail ? playlistDetail.name : '平台歌单' }}</div>
                        <div class="discover-sub">{{ playlistView === 'detail' ? playlistDetailMeta : '搜索 QQ 歌单，也可直接输入歌单 ID' }}</div>
                    </div>
                    <button type="button" class="discover-close" title="关闭" @click="openPlaylist = false">
                        <mu-icon value="close" :size="20"></mu-icon>
                    </button>
                </div>
                <div v-if="playlistView === 'list'" class="discover-pane">
                    <div class="discover-toolbar">
                        <span class="discover-toolbar-label">歌单搜索</span>
                        <div class="discover-toolbar-actions">
                            <button type="button" class="discover-chip" :class="{ 'is-solid': playlistType === 'hot' }" @click="loadPlaylistShortcut('hot')">热门歌单</button>
                            <button type="button" class="discover-chip" :class="{ 'is-solid': playlistType === 'official' }" @click="loadPlaylistShortcut('official')">官方歌单</button>
                        </div>
                    </div>
                    <div class="discover-search-row">
                        <input
                            class="discover-input"
                            v-model.trim="playlistKeyword"
                            @keydown.enter.prevent="searchPlaylists"
                            placeholder="歌单名称或歌单 ID"
                        >
                        <button type="button" class="discover-search-btn" @click="searchPlaylists">搜索</button>
                    </div>
                    <div class="discover-stats">
                        <span>已显示 <b>{{ playlistShownCount }}</b> 个歌单</span>
                        <span>共 {{ playlistCount || 0 }} 条</span>
                    </div>
                    <div class="discover-list" v-if="playlistList && playlistList.length">
                        <div
                            class="discover-row is-link"
                            v-for="(row, index) in playlistList"
                            :key="listRowKey(row, index)"
                            @click="openPlaylistDetail(row)">
                            <div class="discover-cover">
                                <img :src="playlistCover(row)" alt="" @error="onCoverError">
                            </div>
                            <div class="discover-info">
                                <div class="discover-name">{{ row.name }}</div>
                                <div class="discover-meta">
                                    {{ row.creator || '未知创建者' }}
                                    <template v-if="row.songCount"> · {{ row.songCount }} 首</template>
                                </div>
                            </div>
                            <div class="discover-actions">
                                <button type="button" class="discover-chip" @click.stop="openPlaylistDetail(row)">查看</button>
                            </div>
                        </div>
                        <infinite-footer
                            :loading="playlistLoading"
                            :done="!playlistHasMore"
                            @load="loadMorePlaylists"></infinite-footer>
                    </div>
                    <div class="discover-empty" v-else>
                        <div class="discover-empty-title">{{ playlistLoading ? '加载中…' : '暂无歌单' }}</div>
                        <div class="discover-empty-desc">{{ playlistLoading ? '正在读取 QQ 歌单' : (playlistError || '试试搜索歌单名，或输入歌单 ID') }}</div>
                    </div>
                </div>
                <div v-else class="discover-pane">
                    <div class="discover-filter-row">
                        <button type="button" class="discover-chip" @click="backToPlaylistList">返回歌单列表</button>
                    </div>
                    <div class="discover-stats">
                        <span>已显示 <b>{{ playlistSongs.length }}</b> 首歌曲</span>
                        <span>共 {{ playlistSongTotal }} 条</span>
                    </div>
                    <div class="discover-list" v-if="playlistSongs.length">
                        <div
                            class="discover-row"
                            v-for="(row, index) in playlistSongs"
                            :key="listRowKey(row, index)">
                            <div class="discover-cover">
                                <img :src="searchCover(row)" alt="" @error="onCoverError">
                            </div>
                            <div class="discover-info">
                                <div class="discover-name">{{ row.name }}</div>
                                <div class="discover-meta">
                                    {{ row.artist || '未知歌手' }}
                                    <template v-if="searchAlbumName(row)"> · {{ searchAlbumName(row) }}</template>
                                </div>
                            </div>
                            <div class="discover-actions">
                                <button
                                    type="button"
                                    class="room-fav-btn"
                                    :class="{ 'is-on': isSongFavorite(row) }"
                                    :title="isSongFavorite(row) ? '取消收藏' : '收藏'"
                                    @click.stop="toggleFavoriteSong(row)">
                                    <mu-icon :value="isSongFavorite(row) ? 'favorite' : 'favorite_border'" :size="18"></mu-icon>
                                </button>
                                <button type="button" class="discover-pick-btn" @click.stop="tryPickMusic(row)">点歌</button>
                            </div>
                        </div>
                        <infinite-footer
                            :loading="playlistSongsLoading"
                            :done="!playlistSongsHasMore"
                            @load="loadMorePlaylistSongs"></infinite-footer>
                    </div>
                    <div class="discover-empty" v-else>
                        <div class="discover-empty-title">{{ playlistLoading ? '加载中…' : '这个歌单没有歌曲' }}</div>
                        <div class="discover-empty-desc">{{ playlistLoading ? '正在读取歌单歌曲' : (playlistError || '换一个歌单试试') }}</div>
                    </div>
                </div>
            </div>
        </mu-dialog>
    </div>
</template>

<script>

    import {mapGetters, mapMutations} from 'vuex'
    import SockJS from 'sockjs-client'
    import Stomp from 'stompjs'
    import {sendUtils, messageUtils, timeUtils, musicUtils, formatDisplayName} from '../utils'
    import {
        listFavorites,
        isFavorite,
        toggleFavorite,
        clearFavorites,
        mergeFavorites,
        FAVORITES_KEY
    } from '../favorites'
    import {baseUrl, isProduction, shareUrl} from "../config/environment";
    import Navigation from '../components/Navigation'
    import HistoryPanel from '../components/HistoryPanel'
    import InfiniteFooter from '../components/InfiniteFooter'

    export default {
        name: "Music",
        components: {
            Navigation,
            HistoryPanel,
            InfiniteFooter
        },
        computed: {
            ...mapGetters({
                isContented: 'getIsConnected',
                online: 'getSocketOnline',
                chatMessage: 'getChatMessage',
                chatData: 'getChatData',
                music: 'getPlayerMusic',
                progress: 'getPlayerProgress',
                playerTime: 'getPlayerTime',
                // volume: 'getPlayerVolume',
                pick: 'getPlayerPick',
                lyric: 'getPlayerLyric',
                isRoot: 'isSocketRoot',
                isAdmin: 'isSocketAdmin',
		good:'isSocketGood',
                searchKeyword: 'getSearchKeyword',
                searchData: 'getSearchData',
                searchCount: 'getSearchCount',
		music2:'getMusic2',
                historyList: 'getHistoryList',
                lyrics: 'getPlayerLyrics',
                userName: 'getSocketUserName'
            }),
            displayNickName: function () {
                return formatDisplayName(this.userName);
            },
            onlineMemberList: function () {
                const raw = this.normalizeOnlineUsers(this.serverOnlineMembers);
                const list = [];
                const seen = {};
                raw.forEach((item, index) => {
                    if (this.isSystemMember(item)) return;
                    const sid = String(item.sessionId || item.session_id || '').trim();
                    const maskedIp = this.formatMemberIp(item);
                    const key = sid || maskedIp || ('row-' + index);
                    if (seen[key]) return;
                    seen[key] = true;
                    list.push({
                        sessionId: sid,
                        nickName: item.nickName || item.nickname || item.name || '',
                        name: item.name || '',
                        displayName: this.formatMemberName(item),
                        maskedIp: maskedIp
                    });
                });
                return list;
            },
            onlineMemberTotal: function () {
                return Number(this.online) || 0;
            },
            playlistSongTotal: function () {
                if (this.playlistSongsAll && this.playlistSongsAll.length) {
                    return this.playlistSongsAll.length;
                }
                if (this.playlistDetail && this.playlistDetail.songCount) {
                    return this.playlistDetail.songCount;
                }
                return this.playlistSongs.length;
            },
            playlistSongsHasMore: function () {
                return this.playlistSongs.length < this.playlistSongTotal;
            },
            ...mapMutations({
                // volume: 'setPlayerVolume'
            }),
            volume: {
                get: function () {
                    return this.$store.getters.getPlayerVolume;
                },
                set: function (value) {
                    music.volume = Number(value) / 100;
                    this.$store.commit('setPlayerVolume', value);
                }
            },
            audioPlaybackSrc: function () {
                if (this.recoverGaveUp) {
                    return '';
                }
                if (this.audioSrcOverride) {
                    return this.audioSrcOverride;
                }
                return this.resolvePlaybackUrl(this.music && this.music.url);
            },
            coverSrc: function () {
                return (this.music && this.music.pictureUrl)
                    ? this.music.pictureUrl
                    : require('../assets/images/logo.png');
            },
            lyricLines: function () {
                const lyrics = this.lyrics;
                if (!lyrics || typeof lyrics !== 'object') {
                    return [];
                }
                return Object.keys(lyrics)
                    .filter(function (k) { return lyrics[k]; })
                    .map(function (k) {
                        return { time: Number(k), text: lyrics[k] };
                    })
                    .sort(function (a, b) { return a.time - b.time; })
                    .map(function (line, index) {
                        line.index = index;
                        return line;
                    });
            },
            searchShownCount: function () {
                return (this.searchData && this.searchData.length) ? this.searchData.length : 0;
            },
            hotShownCount: function () {
                return (this.hotList && this.hotList.length) ? this.hotList.length : 0;
            },
            isCurrentFavorite: function () {
                return this.isSongFavorite(this.music);
            },
            favoriteShownCount: function () {
                return this.favoriteList.length;
            },
            playlistShownCount: function () {
                return (this.playlistList && this.playlistList.length) ? this.playlistList.length : 0;
            },
            playlistDetailMeta: function () {
                const d = this.playlistDetail;
                if (!d) return '';
                const parts = [];
                if (d.creator) parts.push(d.creator);
                if (d.songCount) parts.push(d.songCount + ' 首');
                return parts.length ? parts.join(' · ') : '歌单歌曲';
            },
            focusCurrentLyric: function () {
                const lines = this.lyricLines || [];
                if (!lines.length) return null;
                const i = this.activeLyricIndex >= 0 ? this.activeLyricIndex : 0;
                return lines[i] || null;
            },
            focusPrevLyric: function () {
                const lines = this.lyricLines || [];
                const i = this.activeLyricIndex;
                if (i > 0 && lines[i - 1]) return lines[i - 1];
                return null;
            },
            focusNextLyric: function () {
                const lines = this.lyricLines || [];
                const i = this.activeLyricIndex >= 0 ? this.activeLyricIndex : 0;
                if (lines[i + 1]) return lines[i + 1];
                return null;
            },
            progressUsedTime: function () {
                const t = String(this.playerTime || '');
                const i = t.indexOf('/');
                if (i < 0) return t.trim() || '00:00';
                return t.slice(0, i).trim() || '00:00';
            },
            progressTotalTime: function () {
                const t = String(this.playerTime || '');
                const i = t.indexOf('/');
                if (i < 0) return '00:00';
                return t.slice(i + 1).trim() || '00:00';
            }
        },
        data: () => ({
            isPlay: false,
            columns: [
                {title: 'ID', name: 'id', width: 80, align: 'left'},
                {title: '歌曲', name: 'name', width: 200, align: 'left'},
                {title: '歌手', name: 'calories', align: 'center'},
                {title: '专辑', name: 'fat', align: 'center'},
                {title: '点歌人', name: 'carbs', align: 'center'},
            ],
            albumRotate: false,
            screenWidth: document.documentElement.clientWidth,
            albumRotateSize: 300,
            albumRotateStyle: '',
            openSearch: false,
            openHistory: false,
            openFavorite: false,
            openFavoriteImport: false,
            favoriteList: [],
            favoriteImportText: '',
            favoritePlayAllTimer: null,
            openPlaylist: false,
            playlistView: 'list',
            playlistType: 'hot',
            playlistKeyword: '',
            playlistList: [],
            playlistCount: 0,
            playlistPage: 1,
            playlistLimit: 20,
            playlistLoading: false,
            playlistHasMore: true,
            playlistError: '',
            playlistReqId: 0,
            playlistDetailReqId: 0,
            playlistDetail: null,
            playlistSongs: [],
            playlistSongsAll: [],
            playlistSongsShown: 20,
            playlistSongsLoading: false,
            openHot: false,
            hotList: [],
            hotCount: 0,
            hotCurrent: 1,
            hotLimit: 20,
            hotLoading: false,
            hotHasMore: true,
            hotError: '',
            hotReqId: 0,
            openProfile: false,
            profileNameDraft: '',
            openOnlineMembers: false,
            serverOnlineMembers: [],
            openCookie: false,
            cookieInput: '',
            cookieSubmitting: false,
            cookieStatusOk: null,
            cookieStatusText: '正在检查 Cookie 状态…',
            cookieChannelHint: '',
            keyword: '',
            current: 1,
            limit: 20,
            pageCount: 7,
            searchLoading: false,
            searchLoadTimer: null,
            searchHasMore: false,
            searchLoadPage: 1,
            searchStaleLeft: 0,
            // 仅 QQ 音乐
	    source: 'qq',
	    secondUrl:"",
	    firstLoaded:0,
            /** 本会话我点过赞的 songId → true（配合再点取消） */
            myLikedMap: {},
            pendingLikeMusicId: null,
            /** 分享：剪贴板失败时展示可长按复制的文案 */
            showEmojiPanel: false,
            chatCaret: 0,
            reactionEmojis: ['😍', '😎', '😭', '🤩', '👎'],
            reactionFloats: [],
            reactionLocked: false,
            reactionLockTimer: null,
            lastReactionSendTime: 0,
            reactionBurst: { emoji: '', count: 0, since: 0, show: false },
            reactionBurstTimer: null,
            reactionPendingQueue: [],
            reactionReplayTimer: null,
            chatEmojiList: ['😀','😂','🤣','😊','😍','😘','😎','🤔','😅','😭','😡','🥺','🤩','😏','😴','🤗','👍','👎','👏','🙏','💪','✌️','🤝','🫶','🎉','🔥','❤️','✨','🎵','🎶','🎤','💯'],
            openShareFallback: false,
            shareFallbackText: '',
            /**
             * 本会话我点过的歌 id → true（纯前端识别「自己的歌」，不依赖账号体系）
             * 在点歌成功后根据 pending 信息写入
             */
            myPickedMap: {},
            /** 即将点歌成功后要认领的曲目信息 */
            pendingMyPick: null,
            /**
             * 自己的歌提示防刷屏：key = `${kind}:${musicId}`，kind=upcoming|playing
             * 同一用户同一首歌每种提示最多一次
             */
            mySongHintShown: {},
            /** 播放恢复：强制换 src 时用（cache-bust），避免 Vue 绑回旧链 */
            audioSrcOverride: '',
            audioRecovering: false,
            stuckTimer: null,
            suppressStuckUntil: 0,
            recoverCount: 0,
            recoverSongId: '',
            recoverGaveUp: false,
            playbackFallbackTried: false,
            lastAudioTime: 0,
            lastAudioTimeAt: 0,
            watchdogTimer: null,
            wsConnecting: false,
            wsManualClose: false,
            wsReconnectTimer: null,
            wsReconnectAttempts: 0,
            wsGeneration: 0,
            wsVerifyTimer: null,
            lastSyncAt: 0,
            lastPlayLogKey: '',
            lastPlayLogAt: 0,
            pausedWhileHidden: false,
            activeNav: 'playing',
            showChat: true,
            mainTab: 'queue',
            isAudioPlaying: true,
            compactMode: false,
            userPaused: false,
            activeLyricIndex: -1,
            chatPinnedToBottom: true,
            queuePinnedToBottom: false
        }),
        methods: {
            play: function () {
                this.getScreenWidth();
                this.isPlay = !this.isPlay;
                this.lastAudioTimeAt = Date.now();
                this.suppressStuckUntil = Date.now() + 10000;
                this.startPlaybackWatchdog();
                this.connect();
                this.$nextTick(() => {
                    this.scheduleMobileTabLayout();
                });
            },
            selectNav: function (key) {
                this.activeNav = key;
                if (key === 'search') {
                    this.openSearch = true;
                } else if (key === 'playlist') {
                    this.openPlaylistDialog();
                } else if (key === 'favorite') {
                    this.openFavoriteDialog();
                } else if (key === 'history') {
                    this.rememberCurrentSession(this.$store.getters.getSocketClient, this.$store.getters.getStompClient);
                    this.openHistory = true;
                }
            },
            selectMainTab: function (key) {
                this.mainTab = key;
                this.scheduleMobileTabLayout();
                if (key === 'hot') {
                    this.loadHotTab();
                    return;
                }
                if (key === 'favorite') {
                    this.reloadFavorites();
                    return;
                }
                if (key === 'lyrics') {
                    this.$nextTick(() => {
                        this.scrollActiveLyricIntoView();
                    });
                }
            },
            isMobileTabLayout: function () {
                const w = (window.innerWidth || document.documentElement.clientWidth || this.screenWidth || 0);
                return w <= 766;
            },
            applyMobileViewportHeight: function () {
                const root = document.documentElement;
                if (!root) return;
                if (!this.isMobileTabLayout()) {
                    root.style.removeProperty('--room-vv-h');
                    return;
                }
                const vv = window.visualViewport;
                const h = Math.round((vv && vv.height) || window.innerHeight || 0);
                if (!h) return;
                root.style.setProperty('--room-vv-h', h + 'px');
            },
            syncMobileTabBodyHeight: function () {
                this.applyMobileViewportHeight();
                const app = this.$el && this.$el.querySelector
                    ? this.$el.querySelector('.room-app')
                    : document.querySelector('.room-app');
                if (!app) return;
                if (!this.isPlay || this.compactMode || !this.isMobileTabLayout()) {
                    app.style.removeProperty('--room-tab-body-h');
                    return;
                }
                const vv = window.visualViewport;
                const vh = Math.round((vv && vv.height) || window.innerHeight || 0);
                const topbar = document.querySelector('.room-topbar');
                const player = app.querySelector('.room-nowplaying');
                const tabs = app.querySelector('.room-tabs');
                const main = app.querySelector('.room-main');
                const panel = app.querySelector('.room-list-panel');
                const rectH = function (el) {
                    return el ? Math.round(el.getBoundingClientRect().height) : 0;
                };
                const vPad = function (el) {
                    if (!el) return 0;
                    const cs = window.getComputedStyle(el);
                    return (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
                };
                const topbarH = rectH(topbar) || 60;
                const playerBlock = rectH(player) + (player ? (parseFloat(window.getComputedStyle(player).marginBottom) || 0) : 0);
                const tabsH = rectH(tabs) || 44;
                const mainPad = vPad(main);
                const panelPad = vPad(panel);
                const minH = 160;
                const maxH = Math.max(minH, Math.round(vh * 0.42));
                let h = vh - topbarH - playerBlock - tabsH - mainPad - panelPad;
                if (!isFinite(h) || h < minH) {
                    h = Math.max(minH, Math.round(vh * 0.28));
                }
                h = Math.max(minH, Math.min(maxH, h));
                const next = Math.round(h) + 'px';
                if (app.style.getPropertyValue('--room-tab-body-h') === next) return;
                app.style.setProperty('--room-tab-body-h', next);
            },
            scheduleMobileTabLayout: function () {
                const self = this;
                const run = function () {
                    self.applyMobileViewportHeight();
                    self.syncMobileTabBodyHeight();
                };
                run();
                this.$nextTick(run);
                requestAnimationFrame(function () {
                    run();
                    requestAnimationFrame(run);
                });
                if (this._mobileTabLayoutLater) {
                    clearTimeout(this._mobileTabLayoutLater);
                }
                this._mobileTabLayoutLater = setTimeout(run, 160);
                if (this._mobileTabLayoutLate2) {
                    clearTimeout(this._mobileTabLayoutLate2);
                }
                this._mobileTabLayoutLate2 = setTimeout(run, 480);
            },
            bindMobileTabLayout: function () {
                const self = this;
                if (!this._onMobileTabLayout) {
                    this._onMobileTabLayout = function () {
                        if (self._mobileTabLayoutTimer) {
                            clearTimeout(self._mobileTabLayoutTimer);
                        }
                        self._mobileTabLayoutTimer = setTimeout(function () {
                            self.scheduleMobileTabLayout();
                        }, 50);
                    };
                    window.addEventListener('resize', this._onMobileTabLayout);
                    window.addEventListener('orientationchange', this._onMobileTabLayout);
                    window.addEventListener('pageshow', this._onMobileTabLayout);
                    window.addEventListener('load', this._onMobileTabLayout);
                    if (window.visualViewport) {
                        window.visualViewport.addEventListener('resize', this._onMobileTabLayout);
                        window.visualViewport.addEventListener('scroll', this._onMobileTabLayout);
                    }
                }
                this.scheduleMobileTabLayout();
            },
            unbindMobileTabLayout: function () {
                if (this._mobileTabLayoutTimer) {
                    clearTimeout(this._mobileTabLayoutTimer);
                    this._mobileTabLayoutTimer = null;
                }
                if (this._mobileTabLayoutLater) {
                    clearTimeout(this._mobileTabLayoutLater);
                    this._mobileTabLayoutLater = null;
                }
                if (this._mobileTabLayoutLate2) {
                    clearTimeout(this._mobileTabLayoutLate2);
                    this._mobileTabLayoutLate2 = null;
                }
                if (!this._onMobileTabLayout) return;
                window.removeEventListener('resize', this._onMobileTabLayout);
                window.removeEventListener('orientationchange', this._onMobileTabLayout);
                window.removeEventListener('pageshow', this._onMobileTabLayout);
                window.removeEventListener('load', this._onMobileTabLayout);
                if (window.visualViewport) {
                    window.visualViewport.removeEventListener('resize', this._onMobileTabLayout);
                    window.visualViewport.removeEventListener('scroll', this._onMobileTabLayout);
                }
                this._onMobileTabLayout = null;
            },
            getQueueScroller: function () {
                const list = this.$refs.queueList;
                if (!list) return null;
                const pane = list.closest ? list.closest('.room-queue-pane') : null;
                const canScroll = function (el) {
                    if (!el) return false;
                    const oy = window.getComputedStyle(el).overflowY;
                    return oy === 'auto' || oy === 'scroll' || oy === 'overlay';
                };
                if (canScroll(list)) return list;
                if (canScroll(pane)) return pane;
                return list;
            },
            onQueueListScroll: function (e) {
                const el = e && e.target;
                if (!el) return;
                const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
                this.queuePinnedToBottom = gap <= 48;
            },
            maybeScrollQueueToBottom: function (force) {
                const scroller = this.getQueueScroller();
                if (!scroller) return;
                if (force || this.queuePinnedToBottom) {
                    scroller.scrollTop = scroller.scrollHeight;
                }
            },
            scrollActiveLyricIntoView: function () {
                const wrap = this.$el && this.$el.querySelector('.room-lyric-wrap');
                const active = wrap && wrap.querySelector('.room-lyric-line.is-active');
                if (!wrap || !active) return;
                const wrapRect = wrap.getBoundingClientRect();
                const lineRect = active.getBoundingClientRect();
                const delta = (lineRect.top + lineRect.height / 2) - (wrapRect.top + wrapRect.height / 2);
                wrap.scrollTop += delta;
            },
            toggleChat: function () {
                this.showChat = !this.showChat;
                if (this.showChat) {
                    this.chatPinnedToBottom = true;
                    this.$nextTick(() => {
                        this.maybeScrollChatToBottom(true);
                    });
                }
            },
            onChatListScroll: function (e) {
                const el = e && e.target;
                if (!el) return;
                const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
                this.chatPinnedToBottom = gap <= 48;
            },
            maybeScrollChatToBottom: function (force) {
                const chat = document.querySelector('#chat-container');
                if (!chat) return;
                if (force || this.chatPinnedToBottom) {
                    chat.scrollTop = chat.scrollHeight;
                }
            },
            toggleCompactMode: function () {
                this.compactMode = !this.compactMode;
            },
            onFocusHotkey: function (e) {
                if (e && e.key === 'Escape' && this.compactMode) {
                    this.compactMode = false;
                }
            },
            openProfileDialog: function () {
                const name = this.userName;
                this.profileNameDraft = (!name || name === 'null' || name === 'undefined') ? '' : name;
                this.openProfile = true;
            },
            saveProfileName: function () {
                this.applyNickNameChange(this.profileNameDraft);
            },
            reloadFavorites: function () {
                this.favoriteList = listFavorites();
            },
            isSongFavorite: function (row) {
                if (!row || row.id == null || row.id === 'placeholder') return false;
                return isFavorite(row.id, this.favoriteList);
            },
            toggleFavoriteSong: function (row) {
                const result = toggleFavorite(row);
                this.favoriteList = result.list;
                if (!row || !row.id) {
                    this.$toast && this.$toast.message('没有可收藏的歌曲');
                    return;
                }
                this.$toast && this.$toast.message(result.added ? ('已收藏：' + (row.name || '')) : ('已取消收藏：' + (row.name || '')));
            },
            toggleFavoriteCurrent: function () {
                if (!this.music || !this.music.id || this.music.id === 'placeholder') {
                    this.$toast && this.$toast.message('暂无正在播放的歌曲');
                    return;
                }
                this.toggleFavoriteSong(this.music);
            },
            openFavoriteDialog: function () {
                this.reloadFavorites();
                this.openFavorite = true;
            },
            playAllFavorites: function () {
                const list = this.favoriteList || [];
                if (!list.length) {
                    this.$toast && this.$toast.message('暂无收藏歌曲');
                    return;
                }
                const picked = {};
                (this.pick || []).forEach((item) => {
                    if (item && item.id != null) picked[String(item.id)] = true;
                });
                const pending = list.filter((item) => item && item.id && !picked[String(item.id)]);
                if (!pending.length) {
                    this.$toast && this.$toast.message('收藏里的歌都已在待播队列');
                    return;
                }
                if (this.favoritePlayAllTimer) {
                    clearTimeout(this.favoritePlayAllTimer);
                    this.favoritePlayAllTimer = null;
                }
                let i = 0;
                const _this = this;
                const step = function () {
                    if (i >= pending.length) {
                        _this.favoritePlayAllTimer = null;
                        return;
                    }
                    _this.pickMusic(pending[i]);
                    i += 1;
                    if (i < pending.length) {
                        _this.favoritePlayAllTimer = setTimeout(step, 600);
                    }
                };
                this.$toast && this.$toast.message('正在加入 ' + pending.length + ' 首收藏');
                step();
            },
            exportFavorites: function () {
                const list = this.favoriteList || [];
                if (!list.length) {
                    this.$toast && this.$toast.message('暂无收藏可导出');
                    return;
                }
                const text = JSON.stringify(list, null, 2);
                try {
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText(text);
                    }
                } catch (e) { /* ignore */ }
                try {
                    const blob = new Blob([text], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = FAVORITES_KEY + '.json';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    this.$toast && this.$toast.success('已导出收藏文件，并尝试复制到剪贴板');
                } catch (e) {
                    this.$toast && this.$toast.error('导出失败');
                }
            },
            pickFavoriteFile: function () {
                const input = this.$refs.favoriteFile;
                if (input) input.click();
            },
            onFavoriteFileChange: function (e) {
                const file = e && e.target && e.target.files && e.target.files[0];
                if (!file) return;
                const reader = new FileReader();
                const _this = this;
                reader.onload = function () {
                    _this.favoriteImportText = String(reader.result || '');
                };
                reader.readAsText(file, 'utf-8');
                e.target.value = '';
            },
            importFavoritesFromText: function () {
                let parsed;
                try {
                    parsed = JSON.parse(this.favoriteImportText || '');
                } catch (e) {
                    this.$toast && this.$toast.error('JSON 格式不对');
                    return;
                }
                const incoming = Array.isArray(parsed) ? parsed : (parsed && Array.isArray(parsed.list) ? parsed.list : null);
                if (!incoming) {
                    this.$toast && this.$toast.error('需要一个歌曲数组');
                    return;
                }
                this.favoriteList = mergeFavorites(incoming);
                this.openFavoriteImport = false;
                this.favoriteImportText = '';
                this.$toast && this.$toast.success('已导入，当前共 ' + this.favoriteList.length + ' 首');
            },
            clearFavoriteList: function () {
                if (!this.favoriteList.length) {
                    this.$toast && this.$toast.message('收藏已经是空的');
                    return;
                }
                if (!window.confirm('确定清空当前浏览器的全部收藏？')) {
                    return;
                }
                this.favoriteList = clearFavorites();
                this.$toast && this.$toast.message('已清空收藏');
            },
            formatChatUser: function (item) {
                const name = formatDisplayName(item && item.nickName);
                if ((this.isRoot || this.isAdmin) && item && item.type === 'chat' && item.sessionId) {
                    return name + '[' + item.sessionId + ']';
                }
                return name;
            },
            formatQueueUser: function (row) {
                const name = formatDisplayName(row && row.nickName);
                if ((this.isRoot || this.isAdmin) && row && row.sessionId) {
                    return name + '[' + row.sessionId + ']';
                }
                return name;
            },
            formatMemberName: function (item) {
                if (!item) return formatDisplayName('');
                const raw = item.nickName || item.nickname || item.displayName || item.name || '';
                return formatDisplayName(raw);
            },
            formatMemberIp: function (item) {
                if (!item) return '';
                const direct = String(item.maskedIp || item.ip || item.remoteAddress || item.remote_address || '').trim();
                if (direct && direct !== 'null' && direct !== 'undefined') {
                    return this.maskDisplayIp(direct);
                }
                const rawNick = String(item.nickName || item.nickname || item.displayName || '').trim();
                const parsed = this.splitNickAndIp(rawNick);
                return parsed.ip || '';
            },
            splitNickAndIp: function (text) {
                const value = String(text || '').trim();
                if (!value || value === 'null' || value === 'undefined') {
                    return { name: '', ip: '' };
                }
                const matched = value.match(/^(.*)\(([^()]*)\)\s*$/);
                if (matched) {
                    const name = String(matched[1] || '').trim();
                    const ip = String(matched[2] || '').trim();
                    if (ip && (ip.indexOf('.') >= 0 || ip.indexOf('*') >= 0 || ip.indexOf(':') >= 0)) {
                        return { name: name, ip: ip };
                    }
                }
                if (value === '()') {
                    return { name: '', ip: '' };
                }
                return { name: value, ip: '' };
            },
            maskDisplayIp: function (ip) {
                const value = String(ip || '').trim();
                if (!value) return '';
                if (value.indexOf('*') >= 0) return value;
                const parts = value.split('.');
                if (parts.length === 4) {
                    return parts[0] + '.' + parts[1] + '.*.*';
                }
                return value;
            },
            extractOnlinePayload: function (data) {
                const result = { count: null, users: null };
                if (data === null || typeof data === 'undefined' || data === '') {
                    return result;
                }
                if (typeof data === 'number') {
                    result.count = data;
                    return result;
                }
                if (Array.isArray(data)) {
                    result.users = this.normalizeOnlineUsers(data);
                    return result;
                }
                if (typeof data !== 'object') {
                    const asNumber = Number(data);
                    if (!isNaN(asNumber)) result.count = asNumber;
                    return result;
                }
                if (data.count !== undefined && data.count !== null && data.count !== '') {
                    result.count = Number(data.count);
                    if (isNaN(result.count)) result.count = 0;
                }
                const users = data.users || data.members || data.onlineUsers || data.list;
                if (users !== undefined && users !== null) {
                    result.users = this.normalizeOnlineUsers(users);
                }
                return result;
            },
            normalizeOnlineUsers: function (users) {
                if (Array.isArray(users)) {
                    return users.filter(Boolean);
                }
                if (users && typeof users === 'object') {
                    return Object.keys(users).map((k) => users[k]).filter(Boolean);
                }
                return [];
            },
            isSystemMember: function (item) {
                if (!item) return false;
                const sid = String(item.sessionId || item.session_id || '').trim().toLowerCase();
                const nick = String(item.nickName || item.displayName || item.nickname || '').trim().toLowerCase();
                const name = String(item.name || '').trim().toLowerCase();
                return sid === 'system' || nick === 'system' || name === 'system';
            },
            isCurrentMember: function (member) {
                if (!member) return false;
                const meIds = this.$store.getters.getMeSessionIds || [];
                const sid = String(member.sessionId || '');
                if (sid && meIds.indexOf(sid) !== -1) return true;
                const meNicks = this.$store.getters.getMeNickNames || [];
                const nick = String(member.nickName || '').trim();
                if (nick && meNicks.indexOf(nick) !== -1) return true;
                const mine = String(this.displayNickName || '').trim();
                if (mine && mine !== '未设置昵称' && nick && nick === mine) return true;
                return false;
            },
            memberAvatar: function (key) {
                const emojis = ['🐶', '🐸', '🐺', '🐱', '🐯', '🐹', '🦁', '🐻', '🦊', '🐼', '🐨', '🐵'];
                const text = String(key || 'user');
                let hash = 0;
                for (let i = 0; i < text.length; i++) {
                    hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
                }
                return emojis[hash % emojis.length];
            },
            memberAvatarStyle: function (key) {
                const hues = [12, 28, 152, 188, 210, 262, 320, 18];
                const text = String(key || 'user');
                let hash = 0;
                for (let i = 0; i < text.length; i++) {
                    hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
                }
                const hue = hues[hash % hues.length];
                return {
                    background: 'linear-gradient(145deg, hsl(' + hue + ', 42%, 82%) 0%, hsl(' + hue + ', 28%, 62%) 100%)'
                };
            },
            showOnlineMembers: function () {
                this.openOnlineMembers = true;
                this.refreshOnlineMembers(true);
            },
            refreshOnlineMembers: function (silent) {
                const stompClient = this.$store.getters.getStompClient;
                if (this.isContented && stompClient && typeof stompClient.send === 'function') {
                    try {
                        stompClient.send('/chat/members', {}, JSON.stringify({ sendTime: Date.now() }));
                    } catch (e) { /* ignore */ }
                }
                if (!silent) {
                    this.$toast && this.$toast.message('已刷新成员');
                }
            },
            clearLocalChat: function () {
                this.$store.commit('clearChatData');
                this.chatPinnedToBottom = true;
                this.$toast && this.$toast.success('已清空聊天显示');
            },
            formatQueueIndex: function (index) {
                const n = Number(index) + 1;
                if (!isFinite(n) || n < 1) return '01';
                return n < 10 ? ('0' + n) : String(n);
            },
            copyQueueSongName: async function (row) {
                const name = row && row.name ? String(row.name).trim() : '';
                if (!name) {
                    this.$toast && this.$toast.message('没有可复制的歌名');
                    return;
                }
                const ok = await this.copyTextToClipboard(name);
                if (ok) {
                    this.$toast && this.$toast.success('已复制歌名');
                } else {
                    this.$toast && this.$toast.error('复制失败');
                }
            },
            isMyQueueSong: function (row) {
                if (!row) return false;
                const sid = String(row.sessionId || row.pickUserId || '').trim();
                const meIds = this.$store.getters.getMeSessionIds || [];
                if (sid && meIds.indexOf(sid) !== -1) return true;
                return this.isMyPicked(row.id);
            },
            canRemovePick: function (row, index) {
                if (!row || row.id === 'placeholder') return false;
                if (index === 0 || this.isCurrentPick(row, index)) return false;
                if (this.isRoot || this.isAdmin) return true;
                return this.isMyQueueSong(row);
            },
            removePickMusic: function (row, index) {
                if (index === 0 || this.isCurrentPick(row, index)) {
                    this.$toast && this.$toast.message('正在播放的歌不能从待播删除');
                    return;
                }
                if (!this.canRemovePick(row, index)) {
                    this.$toast && this.$toast.message('只能删除自己点的歌');
                    return;
                }
                const name = (row && row.name) ? row.name : '这首歌';
                if (!window.confirm('确定将《' + name + '》从待播移除？')) {
                    return;
                }
                const stompClient = this.$store.getters.getStompClient;
                if (!this.isContented || !stompClient || typeof stompClient.send !== 'function') {
                    this.$toast && this.$toast.error('尚未连接服务器');
                    return;
                }
                stompClient.send('/music/delete', {}, JSON.stringify({
                    id: row.id,
                    sendTime: Date.now()
                }));
            },
            isCurrentPick: function (row, index) {
                if (this.music && row && row.id != null && this.music.id != null) {
                    return String(this.music.id) === String(row.id);
                }
                return index === 0;
            },
            toggleAudioPlay: function () {
                const audio = this.getAudioEl();
                if (!audio) {
                    return;
                }
                if (audio.paused) {
                    this.userPaused = false;
                    this.isAudioPlaying = true;
                    this.tryPlay(audio);
                } else {
                    this.userPaused = true;
                    this.isAudioPlaying = false;
                    try {
                        audio.pause();
                    } catch (e) { /* ignore */ }
                }
            },
            connect: function () {
                let _this = this;
                if (this.wsConnecting) {
                    return;
                }
                this.clearWsReconnectTimer();
                this.wsManualClose = false;
                this.wsConnecting = true;
                this.wsGeneration = (this.wsGeneration || 0) + 1;
                const generation = this.wsGeneration;

                this.closePreviousSocket();

                let socketClient = new SockJS(baseUrl + '/server'); // new SockJS("https://www.alang.run" + '/wss');//
                let stompClient = Stomp.over(socketClient);

                if (isProduction) {
                    stompClient.debug = () => {};
                }
                if (stompClient.heartbeat) {
                    stompClient.heartbeat.outgoing = 10000;
                    stompClient.heartbeat.incoming = 10000;
                }
                stompClient.connect({}, (frame) => {
                    if (generation !== _this.wsGeneration) {
                        return;
                    }
                    this.wsConnecting = false;
                    const wasReconnect = this.wsReconnectAttempts > 0;
                    this.wsReconnectAttempts = 0;
                    this.$store.commit('setSocketIsConnected', true);

                    // 后端推的是自定义 STOMP 命令（SEARCH/MUSIC/PICK...），必须先吃掉再交给 stompjs
                    this.installCustomFrameHook(socketClient);

                    // pre onclose：自动重连，重连成功后再拉当前曲+进度
                    let afterOnclose = socketClient.onclose;
                    socketClient.onclose = function (e) {
                        if (generation !== _this.wsGeneration) {
                            if (typeof afterOnclose === 'function') {
                                afterOnclose(e);
                            }
                            return;
                        }
                        if (e.type === 'close') {
                            _this.$store.commit('setSocketIsConnected', false);
                            if (!_this.wsManualClose && _this.isPlay) {
                                _this.scheduleWsReconnect();
                            } else {
                                _this.$store.commit('pushChatData', {
                                    type: 'notice',
                                    content: '网络异常, 请尝试重新连接服务器!'
                                });
                                _this.$toast && _this.$toast.error('网络异常, 请尝试重新连接服务器!');
                            }
                        }
                        if (typeof afterOnclose === 'function') {
                            afterOnclose(e);
                        }
                    };

                    let userName = window.localStorage.getItem('USER_NAME');
                    if (userName) {
                        this.settingName(userName);
                    }
                    this.rememberCurrentSession(socketClient, stompClient);

                    this.subscribe();
                    this.afterWsConnected(wasReconnect);

                }, (error) => {
                    if (generation !== _this.wsGeneration) {
                        return;
                    }
                    this.wsConnecting = false;
                    this.$store.commit('setSocketIsConnected', false);
                    if (this.isPlay && !this.wsManualClose) {
                        this.scheduleWsReconnect();
                    } else {
                        this.$toast && this.$toast.error('连接服务器失败，请稍后重试');
                    }
                });

                this.saveSocket(socketClient, stompClient);
            },
            closePreviousSocket: function () {
                const socketClient = this.$store.getters.getSocketClient;
                const stompClient = this.$store.getters.getStompClient;
                try {
                    if (stompClient && typeof stompClient.disconnect === 'function' && stompClient.connected) {
                        stompClient.disconnect();
                    }
                } catch (e) { /* ignore stale */ }
                try {
                    if (socketClient && typeof socketClient.close === 'function') {
                        socketClient.close();
                    }
                } catch (e2) { /* ignore stale */ }
            },
            close: function () {
                this.wsManualClose = true;
                this.clearWsReconnectTimer();
                this.wsGeneration = (this.wsGeneration || 0) + 1;
                let socketClient = this.$store.getters.getSocketClient;
                let stompClient = this.$store.getters.getStompClient;

                try {
                    if (stompClient && typeof stompClient.disconnect === 'function') {
                        stompClient.disconnect();
                    }
                } catch (e) { /* ignore */ }
                try {
                    if (socketClient && typeof socketClient.close === 'function') {
                        socketClient.close();
                    }
                } catch (e2) { /* ignore */ }

                this.saveSocket(socketClient, stompClient);
            },
            installCustomFrameHook: function (socketClient) {
                if (!socketClient || socketClient._jusicFrameHooked) {
                    return;
                }
                const self = this;
                const afterOnMessage = socketClient.onmessage;
                socketClient.onmessage = function (message) {
                    const raw = message && typeof message.data !== 'undefined' ? message.data : message;
                    try {
                        if (messageUtils.isKnowMessageType(raw)) {
                            self.messageHandler(message);
                            return;
                        }
                    } catch (e) {
                        console.warn('custom stomp frame handler failed', e);
                    }
                    if (typeof afterOnMessage === 'function') {
                        afterOnMessage(message);
                    }
                };
                socketClient._jusicFrameHooked = true;
            },
            subscribe: function () {
                let stompClient = this.$store.getters.getStompClient;

                stompClient.subscribe('/topic/chat', (response) => {
                    // console.log('来自 /topic/chat 频道的消息', response);
                    try {
                        const body = JSON.parse(response.body);
                        const data = body && body.data ? body.data : body;
                        if (data) {
                            if (!data.images) data.images = [];
                            if (!data.type) data.type = 'chat';
                            this.rememberMeFromPickChat(data);
                            this.$store.commit('pushChatData', data);
                        }
                    } catch (e) {
                        // ignore malformed topic payload
                    }
                });

                stompClient.subscribe('/topic/music/order', (response) => {
                    // console.log('来自 /topic/music/order 频道的消息', response);
                });

                this.saveSocket(null, stompClient);
            },
            saveSocket: function (socketClient, stompClient) {
                if (socketClient !== null) {
                    this.$store.commit('setSocketClient', socketClient);
                }
                if (stompClient !== null) {
                    this.$store.commit('setStompClient', stompClient);
                }
            },
            getChatInputEl: function () {
                const field = this.$refs.chatField;
                const root = field && (field.$el || field);
                return root && root.querySelector ? root.querySelector('input, textarea') : null;
            },
            rememberChatCaret: function () {
                const el = this.getChatInputEl();
                if (el && typeof el.selectionStart === 'number') {
                    this.chatCaret = el.selectionStart;
                }
            },
            toggleEmojiPanel: function () {
                this.showEmojiPanel = !this.showEmojiPanel;
                if (this.showEmojiPanel) {
                    this.rememberChatCaret();
                }
            },
            closeEmojiPanel: function () {
                this.showEmojiPanel = false;
            },
            sendReaction: function (emoji, ev) {
                if (this.reactionLocked) return;
                if (!emoji || this.reactionEmojis.indexOf(emoji) < 0) return;
                this.reactionLocked = true;
                if (this.reactionLockTimer) {
                    clearTimeout(this.reactionLockTimer);
                }
                this.reactionLockTimer = setTimeout(() => {
                    this.reactionLocked = false;
                    this.reactionLockTimer = null;
                }, 1000);
                const btn = ev && ev.currentTarget;
                if (btn && btn.classList) {
                    btn.classList.add('is-pressed');
                    setTimeout(function () {
                        btn.classList.remove('is-pressed');
                    }, 100);
                }
                const sendTime = Date.now();
                this.lastReactionSendTime = sendTime;
                this.playReactionFloat(emoji, ev);
                const stompClient = this.$store.getters.getStompClient;
                if (this.isContented && stompClient && typeof stompClient.send === 'function') {
                    stompClient.send('/music/reaction', {}, JSON.stringify({
                        emoji: emoji,
                        nickName: formatDisplayName(this.userName),
                        sendTime: sendTime
                    }));
                }
            },
            playReactionFloat: function (emoji, ev, opts) {
                if (!emoji) return;
                let left = 50;
                const wrap = this.$refs.reactionWrap;
                if (opts && opts.randomX) {
                    left = 8 + Math.random() * 84;
                } else {
                    const fromBtn = ev && ev.currentTarget;
                    let btn = fromBtn;
                    if (!btn && wrap && wrap.querySelectorAll) {
                        const buttons = wrap.querySelectorAll('.room-reaction-btn');
                        const idx = this.reactionEmojis.indexOf(emoji);
                        if (idx >= 0 && buttons[idx]) btn = buttons[idx];
                    }
                    if (wrap && btn && wrap.getBoundingClientRect && btn.getBoundingClientRect) {
                        const wr = wrap.getBoundingClientRect();
                        const br = btn.getBoundingClientRect();
                        if (wr.width > 0) {
                            left = ((br.left + br.width / 2) - wr.left) / wr.width * 100;
                        }
                    }
                    left = Math.max(8, Math.min(92, left));
                }
                const dx = Math.round(Math.random() * 24 - 12);
                let rise = 110;
                const listEl = document.querySelector('#chat-container') || document.querySelector('.room-chat-list');
                if (listEl && listEl.getBoundingClientRect) {
                    const h = listEl.getBoundingClientRect().height || 0;
                    if (h > 0) {
                        rise = Math.round(h * 0.28 + Math.random() * h * 0.1);
                    }
                }
                rise = Math.max(80, Math.min(120, rise));
                const dy = -rise;
                const id = Date.now() + '-' + Math.random().toString(36).slice(2, 8);
                const item = { id: id, emoji: emoji, left: left, dx: dx, dy: dy };
                this.reactionFloats.push(item);
                this.noteReactionBurst(emoji);
                setTimeout(() => {
                    this.removeReactionFloat(id);
                }, 2300);
            },
            onReactionFloatEnd: function (id, ev) {
                const name = ev && ev.animationName ? String(ev.animationName) : '';
                if (name && name.indexOf('room-reaction-rise') === -1) return;
                this.removeReactionFloat(id);
            },
            removeReactionFloat: function (id) {
                const i = this.reactionFloats.findIndex((f) => f.id === id);
                if (i >= 0) this.reactionFloats.splice(i, 1);
            },
            isReactionPageHidden: function () {
                return typeof document !== 'undefined' && !!document.hidden;
            },
            enqueueMissedReaction: function (emoji) {
                if (!emoji) return;
                if (!this.reactionPendingQueue) this.reactionPendingQueue = [];
                if (this.reactionPendingQueue.length >= 6) {
                    this.reactionPendingQueue.shift();
                }
                this.reactionPendingQueue.push(emoji);
            },
            onReactionVisibilityChange: function () {
                if (this.isReactionPageHidden()) return;
                this.startMissedReactionReplay();
            },
            startMissedReactionReplay: function () {
                if (this.reactionReplayTimer) return;
                if (!this.reactionPendingQueue || !this.reactionPendingQueue.length) return;
                this.replayNextMissedReaction();
            },
            replayNextMissedReaction: function () {
                this.reactionReplayTimer = null;
                if (this.isReactionPageHidden()) return;
                if (!this.reactionPendingQueue || !this.reactionPendingQueue.length) {
                    this.reactionPendingQueue = [];
                    return;
                }
                const emoji = this.reactionPendingQueue.shift();
                this.playReactionFloat(emoji, null, { randomX: true });
                if (this.reactionPendingQueue.length) {
                    this.reactionReplayTimer = setTimeout(() => {
                        this.replayNextMissedReaction();
                    }, 350);
                } else {
                    this.reactionPendingQueue = [];
                }
            },
            noteReactionBurst: function (emoji) {
                const now = Date.now();
                if (!this.reactionBurst.since || now - this.reactionBurst.since > 3000 || this.reactionBurst.emoji !== emoji) {
                    this.reactionBurst = { emoji: emoji, count: 1, since: now, show: false };
                } else {
                    this.reactionBurst.count += 1;
                    this.reactionBurst.emoji = emoji;
                }
                if (this.reactionBurst.count >= 3) {
                    this.reactionBurst.show = true;
                    if (this.reactionBurstTimer) {
                        clearTimeout(this.reactionBurstTimer);
                    }
                    this.reactionBurstTimer = setTimeout(() => {
                        this.reactionBurst.show = false;
                        this.reactionBurstTimer = null;
                    }, 1500);
                }
            },
            onDocumentPointer: function (e) {
                if (!this.showEmojiPanel) return;
                const wrap = this.$refs.emojiWrap;
                if (wrap && e && e.target && wrap.contains(e.target)) return;
                this.showEmojiPanel = false;
            },
            insertChatEmoji: function (emo) {
                if (!emo) return;
                const text = this.chatMessage || '';
                let start = typeof this.chatCaret === 'number' ? this.chatCaret : text.length;
                if (start < 0) start = 0;
                if (start > text.length) start = text.length;
                const next = text.slice(0, start) + emo + text.slice(start);
                this.updateChatMessage(next);
                this.chatCaret = start + emo.length;
                this.showEmojiPanel = false;
                const _this = this;
                this.$nextTick(function () {
                    const el = _this.getChatInputEl();
                    if (!el) return;
                    el.focus();
                    if (typeof el.setSelectionRange === 'function') {
                        el.setSelectionRange(_this.chatCaret, _this.chatCaret);
                    }
                });
            },
            sendHandler: function () {
                this.showEmojiPanel = false;
                // console.log('消息发送处理器');
                let stompClient = this.$store.getters.getStompClient;
                let chatMessage = this.$store.getters.getChatMessage;

                if (!this.isContented || !stompClient || typeof stompClient.send !== 'function') {
                    this.$toast && this.$toast.error('尚未连接服务器，请先点击播放或「连接服务器」');
                    return;
                }

                let instruction = sendUtils.parseInstruction(chatMessage);
                let content = '';

                switch (instruction) {
                    case '点歌':
                        content = sendUtils.parseContent(instruction, chatMessage);
                        if (content === '') {
                            // console.log('请输入音乐关键词', chatMessage)
                        } else {
                            this.$store.commit('setPendingPickHint', content);
                            this.pendingMyPick = { name: content, id: null, ts: Date.now() };
                            stompClient.send('/music/pick', {}, JSON.stringify({
                                name: content,
				source: 'qq',
                                sendTime: Date.now()
                            }));
                            this.$toast && this.$toast.message(`正在点歌：${content}`);
                        }
                        break;
                    case '投票切歌':
                        this.musicSkipVote();
                        break;
                    case '设置昵称':
                        content = sendUtils.parseContent(instruction, chatMessage);
                        this.applyNickNameChange(content);
                        break;
                    case 'root':
                        content = sendUtils.parseContent(instruction, chatMessage);
                        if (content === '') {
                            // console.log('请输入 root 密码', chatMessage)
                        } else {
                            stompClient.send('/auth/root', {}, JSON.stringify({
                                password: content,
                                sendTime: Date.now()
                            }));
                        }
                        break;
                    case 'admin':
                        content = sendUtils.parseContent(instruction, chatMessage);
                        if (content === '') {
                            // console.log('请输入 admin 密码', chatMessage)
                        } else {
                            stompClient.send('/auth/admin', {}, JSON.stringify({
                                password: content,
                                sendTime: Date.now()
                            }));
                        }
                        break;
                    case '置顶音乐':
                        content = sendUtils.parseContent(instruction, chatMessage);
                        if (content === '') {
                            // console.log('请输入要置顶的音乐 id', chatMessage)
                        } else {
                            stompClient.send('/music/top', {}, JSON.stringify({
                                id: content,
                                sendTime: Date.now()
                            }));
                        }
                        break;
                    case '删除音乐':
                        content = sendUtils.parseContent(instruction, chatMessage);
                        if (content === '') {
                            // console.log('请输入要删除的音乐 id', chatMessage)
                        } else {
                            stompClient.send('/music/delete', {}, JSON.stringify({
                                id: content,
                                sendTime: Date.now()
                            }));
                        }
                        break;
		    case '清空列表':
		    	 stompClient.send('/music/clear', {}, '');
			 break;
                    case '清空历史':
                         stompClient.send('/music/history/clear', {}, '');
                         break;
                    case '设置Cookie':
                        content = sendUtils.parseContent(instruction, chatMessage);
                        if (content) {
                            stompClient.send('/admin/cookie', {}, JSON.stringify({
                                content: content,
                                sendTime: Date.now()
                            }));
                        }
                        break;
		     case '音乐黑名单':
		    	 stompClient.send('/music/blackmusic', {}, '');
			 break;
		       case '用户黑名单':
		    	 stompClient.send('/chat/blackuser', {}, '');
			 break;
		    case '调整音量':
		        content = sendUtils.parseContent(instruction, chatMessage);
		    	if (content === '') {
			  content = 0;
                        } 

		    	 stompClient.send('/music/volumn/'+content,{},'');
			
			 break;
		      case '点赞模式':
		    	 stompClient.send('/music/goodmodel/true',{},'');
			 break;
		     case '退出点赞模式':
		    	 stompClient.send('/music/goodmodel/false',{},'');
			 break;
		     case '禁止点歌':
		    	 stompClient.send('/music/banchoose/true',{},'');
			 break;
		     case '禁止切歌':
		    	 stompClient.send('/music/banswitch/true',{},'');
			 break;
		      case '启用切歌':
		    	 stompClient.send('/music/banswitch/false',{},'');
			 break;
		     case '启用点歌':
		    	 stompClient.send('/music/banchoose/false',{},'');
			 break;
                    case '拉黑用户':
                        content = sendUtils.parseContent(instruction, chatMessage);
                        if (content === '') {
                            // console.log('请输入要拉黑的用户 session', chatMessage)
                        } else {
                            stompClient.send('/chat/black', {}, JSON.stringify({
                                sessionId: content,
                                sendTime: Date.now()
                            }));
                        }
                        break;
                    case '漂白用户':
                        content = sendUtils.parseContent(instruction, chatMessage);
                        if (content === '') {
                            // console.log('请输入要漂白的用户 session', chatMessage)
                        } else {
                            stompClient.send('/chat/unblack', {}, JSON.stringify({
                                sessionId: content,
                                sendTime: Date.now()
                            }));
                        }
                        break;
                    case '拉黑音乐':
                        content = sendUtils.parseContent(instruction, chatMessage);
                        if (content === '') {
                            // console.log('请输入要拉黑的音乐 id', chatMessage);
                        } else {
                            stompClient.send('/music/black', {}, JSON.stringify({
                                id: content,
                                sendTime: Date.now()
                            }));
                        }
                        break;
                    case '漂白音乐':
                        content = sendUtils.parseContent(instruction, chatMessage);
                        if (content === '') {
                        } else {
                            // console.log('请输入要漂白的音乐 id', chatMessage);
                            stompClient.send('/music/unblack', {}, JSON.stringify({
                                id: content,
                                sendTime: Date.now()
                            }));
                        }
                        break;
                    case '@管理员':
                        content = sendUtils.parseContent(instruction, chatMessage);
                        if (content === '') {
                        } else {
                            stompClient.send('/mail/send', {}, JSON.stringify({
                                content: content,
                                sendTime: Date.now()
                            }));
                        }
                        break;
                    default:
                        if (chatMessage === null || chatMessage === '' || chatMessage.length === 0) {
                            // console.log('消息非法', chatMessage);
                        } else {
                            stompClient.send('/chat', {}, JSON.stringify({
                                content: chatMessage,
                                sendTime: Date.now()
                            }));
                        }
                        break;
                }

                this.$store.commit('setChatMessage', '');
            },
            messageHandler: function (source) {
                const raw = source && typeof source.data !== 'undefined' ? source.data : source;
                if (!messageUtils.isKnowMessageType(raw)) {
                    return;
                }
                const messageType = messageUtils.parseMessageType(raw);
                try {
                    let messageContent = messageUtils.parseMessageContent(raw) || {};
                    switch (messageType) {
                        case messageUtils.messageType.ONLINE: {
                            const parsed = this.extractOnlinePayload(messageContent.data);
                            if (parsed.count !== null && parsed.count !== undefined) {
                                this.$store.commit('setSocketOnline', parsed.count);
                            }
                            if (parsed.users !== null) {
                                this.serverOnlineMembers = parsed.users;
                            }
                            break;
                        }
                        case messageUtils.messageType.NOTICE:
                            if (messageContent.message !== undefined
                                && typeof (messageContent.message) !== 'undefined'
                                && messageContent.message !== null
                                && messageContent.message !== '') {
                                this.$store.commit('pushChatData', {
                                    content: messageContent.message,
                                    type: 'notice'
                                });
                                // 点歌 / 点赞结果 toast（面向操作用户）
                                const msg = String(messageContent.message);
                                const hint = this.$store.getters.getPendingPickHint;
                                if (msg.indexOf('点歌成功') !== -1) {
                                    // 历史面板点歌可能只写了 sessionStorage
                                    this.pullPendingMyPickFromStorage();
                                    // 后端已带歌名：点歌成功：《xxx》；无则用 pending 兜底
                                    let toastMsg = msg;
                                    if (msg === '点歌成功' || msg === '点歌成功：') {
                                        toastMsg = hint ? `点歌成功：《${hint}》` : '点歌成功';
                                    } else if (msg.indexOf('《') === -1 && hint) {
                                        toastMsg = `点歌成功：《${hint}》`;
                                    }
                                    this.$toast && this.$toast.success(toastMsg);
                                    // 从成功文案解析歌名，便于认领「自己的歌」
                                    const nameFromMsg = this.extractSongNameFromNotice(msg) || hint;
                                    if (nameFromMsg && this.pendingMyPick) {
                                        this.pendingMyPick.name = nameFromMsg;
                                    } else if (nameFromMsg && !this.pendingMyPick) {
                                        this.pendingMyPick = { name: nameFromMsg, id: null, ts: Date.now() };
                                    }
                                    // id 认领等下一帧 PICK 列表，避免用旧列表误匹配同名曲
                                    this.$store.commit('setPendingPickHint', null);
                                } else if (msg.indexOf('已在播放列表') !== -1 || msg.indexOf('已在队列中') !== -1) {
                                    const nameInMsg = this.extractSongNameFromNotice(msg) || hint;
                                    this.$toast && this.$toast.message(
                                        nameInMsg ? `已在队列中：《${nameInMsg}》` : '已在播放列表'
                                    );
                                    this.pendingMyPick = null;
                                    this.$store.commit('setPendingPickHint', null);
                                } else if (msg.indexOf('点歌失败') !== -1 || msg.indexOf('暂无此音乐') !== -1 || msg.indexOf('当前禁止点歌') !== -1) {
                                    this.$toast && this.$toast.error(msg);
                                    this.pendingMyPick = null;
                                    this.$store.commit('setPendingPickHint', null);
                                } else if (msg.indexOf('Cookie 已更新') !== -1) {
                                    this.$toast && this.$toast.success(msg);
                                    this.cookieInput = '';
                                    this.cookieSubmitting = false;
                                    this.loadCookieStatus();
                                } else if (msg.indexOf('Cookie') !== -1 && (msg.indexOf('失败') !== -1 || msg.indexOf('没有权限') !== -1 || msg.indexOf('缺少') !== -1 || msg.indexOf('不能为空') !== -1 || msg.indexOf('无权限') !== -1 || msg.indexOf('写入') !== -1)) {
                                    this.$toast && this.$toast.error(msg);
                                    this.cookieSubmitting = false;
                                } else if (msg.indexOf('已点赞') !== -1 || msg.indexOf('点赞成功') !== -1) {
                                    this.$toast && this.$toast.success(msg);
                                    this.markPendingLikeResult(true);
                                } else if (msg.indexOf('已取消点赞') !== -1) {
                                    this.$toast && this.$toast.message(msg);
                                    this.markPendingLikeResult(false);
                                } else if (msg.indexOf('已点赞过') !== -1) {
                                    this.$toast && this.$toast.message(msg);
                                } else if (msg.indexOf('点歌列表未发现此歌') !== -1) {
                                    this.$toast && this.$toast.error(msg);
                                } else if (msg === '删除成功') {
                                    this.$toast && this.$toast.success('已从待播移除');
                                } else if (msg.indexOf('本人暂未点播此歌') !== -1) {
                                    this.$toast && this.$toast.error('只能删除自己点的歌');
                                }
                            }
                            break;
                        case messageUtils.messageType.REACTION: {
                            const react = (messageContent && messageContent.data) || {};
                            const shown = react.emoji;
                            if (!shown || this.reactionEmojis.indexOf(shown) < 0) {
                                break;
                            }
                            if (react.sendTime && Number(react.sendTime) === Number(this.lastReactionSendTime)) {
                                break;
                            }
                            if (this.isReactionPageHidden()) {
                                this.enqueueMissedReaction(shown);
                                break;
                            }
                            this.playReactionFloat(shown);
                            break;
                        }
                        case messageUtils.messageType.CHAT:
                            // parse picture
                            let imgList = [];
                            if (messageContent.data && typeof messageContent.data.content === 'string') {
                                let matchUrlList = messageContent.data.content.match(/picture:\/\/[^\s]*/gi)
                                    || messageContent.data.content.match(/[picture].*?:\/\/[^\s]*/gi);
                                if (matchUrlList !== null) {
                                    for (let i = 0; i < matchUrlList.length; i++) {
                                        imgList.push(matchUrlList[i].replace('picture:', ''));
                                        messageContent.data.content = messageContent.data.content.replace(matchUrlList[i], '');
                                    }
                                }
                            }
                            if (messageContent.data) {
                                messageContent.data.images = imgList;
                                if (!messageContent.data.type) {
                                    messageContent.data.type = 'chat';
                                }
                                this.$store.commit('pushChatData', messageContent.data);
                            }
                            break;
			case messageUtils.messageType.GOODMODEL:
			    var data = messageContent.data;
			    if(data == "GOOD"){
			    	this.$store.commit('setSocketGood', true);
			    }else{
			    	this.$store.commit('setSocketGood', false);
			    }
			   // this.$forceUpdate();
			
			    break;
                        case messageUtils.messageType.PICK:
			    if(messageContent.message == "goodlist"){
			    	this.$store.commit('setSocketGood', true);
			    }
                            this.$store.commit('setPlayerPick', messageContent.data);
                            // 点歌成功后从列表认领「自己的歌」id，并检查下一首提示
                            this.claimMyPickedFromList(messageContent.data);
                            this.maybeHintMySongUpcoming(messageContent.data);
			     if(messageContent.data && messageContent.data.length > 1){
			     	this.secondUrl = messageContent.data[1].url;
				//console.log("this.firstLoaded"+this.firstLoaded);
				if(this.firstLoaded == 1){
				    this.$store.commit('setMusic2', {"url":this.secondUrl});
				}
			     }
                            break;
			case messageUtils.messageType.VOLUMN:
			     //console.log(messageContent.data);
			     music.volume = Number(messageContent.data) / 100;
			     this.$store.commit('setPlayerVolume', messageContent.data);
			     
			     break;
                        case messageUtils.messageType.MUSIC:
			    this.firstLoaded = 0;
                            // 后端 playing 可能为空：写入空对象避免模板访问 music.xxx 崩溃
                            const musicPayload = messageContent.data || {
                                name: '暂无可用音乐',
                                artist: '',
                                album: { name: '' },
                                pictureUrl: '',
                                url: '',
                                lyric: ''
                            };
                            this.$store.commit('setPlayerMusic', musicPayload);
                            // 切到自己点的歌时轻提示一次
                            this.maybeHintMySongPlaying(musicPayload);
                            const musicEl = document.querySelector('#music');
                            if (musicEl) {
                                musicEl.preload = "auto";
                            }
                            this.activeLyricIndex = -1;
                            if (!musicPayload.lyric) {
                                this.$store.commit('setPlayerLyrics', []);
                                this.$store.commit('setPlayerLyric', '暂无歌词');
                            } else {
                                this.$store.commit('setPlayerLyrics', musicUtils.parseLyric(musicPayload.lyric));
                            }
                            document.title = musicPayload.name || '帽子戴歪歪';
                            break;
                        case messageUtils.messageType.AUTH_ROOT:
                            this.$store.commit('pushChatData', {
                                content: messageContent.message,
                                type: 'notice'
                            });
                            if (Number(messageContent.code) === 20000) {
                                this.$store.commit('setSocketRoot', true);
                                this.$toast && this.$toast.success('管理员登录成功');
                            } else {
                                this.$toast && this.$toast.error(messageContent.message || '登录失败');
                            }
                            break;
                        case messageUtils.messageType.AUTH_ADMIN:
                            this.$store.commit('pushChatData', {
                                content: messageContent.message,
                                type: 'notice'
                            });
                            if (Number(messageContent.code) === 20000) {
                                this.$store.commit('setSocketAdmin', true);
                                this.$toast && this.$toast.success('管理员登录成功');
                            } else {
                                this.$toast && this.$toast.error(messageContent.message || '登录失败');
                            }
                            break;
                        case messageUtils.messageType.SETTING_NAME:
                            this.$store.commit('pushChatData', {
                                content: messageContent.message,
                                type: 'notice'
                            });
                            if (Number(messageContent.code) === 20000 && messageContent.data && messageContent.data.name) {
                                this.$store.commit('setSocketUserName', messageContent.data.name);
                                this.$store.commit('rememberMeNickName', messageContent.data.name);
                                this.profileNameDraft = messageContent.data.name;
                                this.openProfile = false;
                                this.$toast && this.$toast.success(messageContent.message || '昵称已更新');
                            } else {
                                this.$toast && this.$toast.error(messageContent.message || '昵称设置失败');
                            }
                            break;
                        case messageUtils.messageType.SEARCH: {
                            if (this.searchStaleLeft > 0) {
                                this.searchStaleLeft -= 1;
                                break;
                            }
                            const page = messageContent.data || {};
                            const list = Array.isArray(page.data) ? page.data : (Array.isArray(page) ? page : []);
                            const count = page.totalSize != null ? page.totalSize : list.length;
                            const incomingPage = Number(page.pageIndex || page.currentPage || this.searchLoadPage || 1);
                            const expectPage = Number(this.searchLoadPage || 1);
                            if (incomingPage !== expectPage) {
                                break;
                            }
                            const fresh = this.uniqBySongId(list);
                            if (incomingPage <= 1) {
                                this.$store.commit('setSearchData', fresh);
                            } else {
                                this.$store.commit('setSearchData', this.uniqBySongId((this.searchData || []).concat(fresh)));
                            }
                            this.$store.commit('setSearchCount', count);
                            const shown = (this.searchData && this.searchData.length) ? this.searchData.length : 0;
                            this.searchHasMore = fresh.length > 0 && shown < Number(count || 0);
                            this.searchLoading = false;
                            this.clearSearchTimeout();
                            break;
                        }
                        case messageUtils.messageType.SEARCH_PICTURE: {
                            const picPage = messageContent.data || {};
                            const picList = Array.isArray(picPage.data) ? picPage.data : (Array.isArray(picPage) ? picPage : []);
                            const picCount = picPage.totalSize != null ? picPage.totalSize : picList.length;
                            this.$store.commit('setSearchPictureCount', picCount);
                            this.$store.commit('setSearchPictureData', picList);
                            break;
                        }
                        case messageUtils.messageType.HISTORY:
                            // data 为历史列表（最新在前）
                            this.$store.commit('setHistoryList', messageContent.data || []);
                            this.rememberMeFromHistory(messageContent.data || []);
                            break;
                        case messageUtils.messageType.PAUSE:
                            break;
                        default:
                            break;
                    }
                } catch (e) {
                    console.warn('stomp messageHandler failed', messageType, e);
                }
            },
            updateChatMessage: function (value) {
                this.$store.commit('setChatMessage', value);
            },
            clearSearchTimeout: function () {
                if (this.searchLoadTimer) {
                    clearTimeout(this.searchLoadTimer);
                    this.searchLoadTimer = null;
                }
            },
            armSearchTimeout: function () {
                this.clearSearchTimeout();
                const _this = this;
                this.searchLoadTimer = setTimeout(function () {
                    _this.searchLoadTimer = null;
                    if (_this.searchLoading) {
                        _this.searchLoading = false;
                        _this.searchHasMore = false;
                    }
                }, 12000);
            },
            updateSearchKeyword: function (value) {
                this.$store.commit('setSearchKeyword', value);
            },
            firstText: function () {
                for (let i = 0; i < arguments.length; i++) {
                    const v = arguments[i];
                    if (v === 0 || v === '0') continue;
                    if (v == null) continue;
                    const s = String(v).trim();
                    if (!s || s === 'null' || s === 'undefined') continue;
                    return s;
                }
                return '';
            },
            isQqSongMid: function (id) {
                return /^[a-zA-Z0-9]{14}$/.test(String(id || ''));
            },
            joinSingerNames: function (singer) {
                if (!singer) return '';
                if (typeof singer === 'string') return singer.trim();
                if (Array.isArray(singer)) {
                    return singer.map((s) => {
                        if (!s) return '';
                        if (typeof s === 'string') return s;
                        return this.firstText(s.name, s.title, s.singer_name);
                    }).filter(Boolean).join(', ');
                }
                if (typeof singer === 'object') {
                    return this.firstText(singer.name, singer.title, singer.nick);
                }
                return '';
            },
            qqAlbumCover: function (albumMid, fallback) {
                const pic = this.firstText(fallback);
                if (pic) return pic;
                const mid = this.firstText(albumMid);
                if (!mid) return '';
                return 'https://y.gtimg.cn/music/photo_new/T002R300x300M000' + mid + '.jpg';
            },
            normalizeQqSong: function (row) {
                if (!row || typeof row !== 'object') return null;
                const album = row.album && typeof row.album === 'object' ? row.album : {};
                const mid = this.firstText(
                    row.songmid,
                    row.mid,
                    this.isQqSongMid(row.id) ? row.id : '',
                    row.media_mid
                );
                if (!mid) return null;
                const albumMid = this.firstText(row.albummid, row.albumMid, album.mid, album.pmid, album.albummid);
                const pic = this.qqAlbumCover(albumMid, this.firstText(
                    row.pictureUrl,
                    row.picture_url,
                    row.cover,
                    row.pic,
                    album.pictureUrl,
                    album.picture_url,
                    album.pic
                ));
                const artist = this.firstText(
                    row.artist,
                    row.singerName,
                    this.joinSingerNames(row.singer),
                    this.joinSingerNames(row.singers)
                );
                let duration = Number(row.duration) || 0;
                if (!duration && row.interval != null) {
                    const interval = Number(row.interval) || 0;
                    duration = interval > 10000 ? interval : interval * 1000;
                }
                return {
                    id: mid,
                    name: this.firstText(row.songname, row.name, row.title, row.song_name, mid),
                    artist: artist || '未知歌手',
                    source: 'qq',
                    pictureUrl: pic,
                    picture_url: pic,
                    duration: duration,
                    privilege: row.privilege || null,
                    album: {
                        name: this.firstText(row.albumname, album.name, album.title),
                        pictureUrl: pic,
                        picture_url: pic,
                        mid: albumMid
                    }
                };
            },
            normalizeQqPlaylist: function (row) {
                if (!row || typeof row !== 'object') return null;
                let creatorObj = row.creator || row.creator_info || {};
                if (typeof creatorObj === 'string') {
                    creatorObj = { name: creatorObj };
                }
                const id = this.firstText(row.tid, row.dissid, row.disstid, row.id);
                if (!id) return null;
                const songIds = Array.isArray(row.song_ids) ? row.song_ids : [];
                const songCount = Number(row.song_num || row.song_count || row.songnum || row.songCount || songIds.length) || 0;
                const listen = Number(row.access_num || row.listennum || row.listenNum || row.visitnum) || 0;
                return {
                    id: String(id),
                    name: this.firstText(row.diss_name, row.dissname, row.title, row.name) || '未命名歌单',
                    cover: this.firstText(
                        row.cover_url_big,
                        row.cover_url_medium,
                        row.cover_url_small,
                        row.logo,
                        row.imgurl,
                        row.cover,
                        row.pic
                    ),
                    creator: this.firstText(
                        creatorObj.name,
                        creatorObj.nick,
                        creatorObj.nickname,
                        row.nickname,
                        row.nick
                    ),
                    songCount: songCount,
                    listenNum: listen
                };
            },
            normalizeQqPlaylistDetail: function (data, fallback) {
                fallback = fallback || {};
                if (!data || typeof data !== 'object') {
                    return { detail: fallback, songs: [] };
                }
                let creator = data.creator;
                if (creator && typeof creator === 'object') {
                    creator = this.firstText(creator.name, creator.nick, creator.nickname);
                }
                const rawSongs = data.songs || data.songlist || data.songList || [];
                const songs = (Array.isArray(rawSongs) ? rawSongs : [])
                    .map((item) => this.normalizeQqSong(item))
                    .filter(Boolean);
                return {
                    detail: {
                        id: this.firstText(data.dissid, data.disstid, data.id, data.tid, fallback.id),
                        name: this.firstText(data.dissname, data.diss_name, data.name, data.title, fallback.name) || '歌单',
                        cover: this.firstText(data.logo, data.dir_pic_url2, data.cover_url_big, data.cover, fallback.cover),
                        creator: this.firstText(creator, data.nickname, data.nick, fallback.creator),
                        songCount: Number(data.songnum || data.total_song_num || data.songCount || data.song_num) || songs.length || fallback.songCount || 0
                    },
                    songs: songs
                };
            },
            searchCover: function (row) {
                if (!row) {
                    return require('../assets/images/logo.png');
                }
                const album = row.album && typeof row.album === 'object' ? row.album : {};
                const pic = this.firstText(
                    row.pictureUrl,
                    row.picture_url,
                    row.cover,
                    row.pic,
                    album.pictureUrl,
                    album.picture_url
                );
                if (pic) return pic;
                const albumMid = this.firstText(row.albummid, row.albumMid, album.mid, album.pmid);
                const built = this.qqAlbumCover(albumMid);
                return built || require('../assets/images/logo.png');
            },
            searchAlbumName: function (row) {
                return row && row.album && row.album.name ? row.album.name : '';
            },
            tryPickMusic: function (row) {
                if (!this.showPickButton(row && row.privilege)) {
                    this.$toast && this.$toast.message('当前音乐不能点播');
                    return;
                }
                this.pickMusic(row);
            },
            onCoverError: function (e) {
                if (e && e.target) {
                    e.target.src = require('../assets/images/logo.png');
                }
            },
            songRowId: function (row) {
                if (!row) return '';
                const id = this.firstText(row.songmid, row.mid, this.isQqSongMid(row.id) ? row.id : '', row.tid, row.dissid, row.id);
                return id;
            },
            listRowKey: function (row, index) {
                const id = this.songRowId(row);
                if (id) return 'id-' + id;
                const name = row && row.name ? String(row.name) : '';
                return 'i-' + index + '-' + name;
            },
            uniqBySongId: function (list) {
                const seen = {};
                const out = [];
                (list || []).forEach((row, i) => {
                    const id = this.songRowId(row);
                    const key = id || ('__anon_' + i + '_' + (row && row.name ? row.name : ''));
                    if (seen[key]) return;
                    seen[key] = true;
                    out.push(row);
                });
                return out;
            },
            openHotChart: function () {
                this.openSearch = false;
                this.openHot = true;
                this.fetchHotChart(1, true);
            },
            loadHotTab: function () {
                if (this.hotLoading) return;
                if (this.hotList && this.hotList.length) return;
                this.fetchHotChart(1, true);
            },
            loadMoreHot: function () {
                if (this.hotLoading || !this.hotHasMore) return;
                this.fetchHotChart((this.hotCurrent || 1) + 1, false);
            },
            fetchHotChart: function (page, reset) {
                if (reset) {
                    this.hotReqId += 1;
                    this.hotCurrent = 1;
                    this.hotList = [];
                    this.hotHasMore = true;
                    this.hotError = '';
                    page = 1;
                } else if (this.hotLoading) {
                    return;
                } else if (!this.hotHasMore) {
                    return;
                }
                const reqId = this.hotReqId;
                const pageNo = page || 1;
                this.hotLoading = true;
                const url = '/api/qq/top?pageNo=' + encodeURIComponent(pageNo) + '&pageSize=' + encodeURIComponent(this.hotLimit);
                fetch(url, { credentials: 'same-origin' })
                    .then((res) => {
                        if (!res.ok) {
                            throw new Error('http ' + res.status);
                        }
                        return res.json();
                    })
                    .then((json) => {
                        if (reqId !== this.hotReqId) return;
                        if (!json || Number(json.result) !== 100 || !json.data) {
                            if (pageNo <= 1) {
                                this.hotList = [];
                                this.hotCount = 0;
                            }
                            this.hotHasMore = false;
                            this.hotError = (json && json.message) ? json.message : '热歌榜暂不可用';
                            return;
                        }
                        const rawList = Array.isArray(json.data.list) ? json.data.list : [];
                        const list = this.uniqBySongId(rawList.map((item) => this.normalizeQqSong(item)).filter(Boolean));
                        this.hotList = pageNo <= 1 ? list : this.uniqBySongId((this.hotList || []).concat(list));
                        this.hotCurrent = pageNo;
                        this.hotCount = Number(json.data.total) || this.hotList.length;
                        this.hotHasMore = list.length > 0 && this.hotList.length < this.hotCount;
                    })
                    .catch(() => {
                        if (reqId !== this.hotReqId) return;
                        if (pageNo <= 1) {
                            this.hotList = [];
                            this.hotCount = 0;
                        }
                        this.hotHasMore = false;
                        this.hotError = '热歌榜获取失败';
                    })
                    .then(() => {
                        if (reqId === this.hotReqId) {
                            this.hotLoading = false;
                        }
                    });
            },
            openPlaylistDialog: function () {
                this.openSearch = false;
                this.openPlaylist = true;
                this.playlistView = 'list';
                if (!this.playlistList.length && !this.playlistLoading) {
                    this.fetchPlaylists(1, true);
                }
            },
            extractPlaylistId: function (text) {
                const raw = String(text || '').trim();
                if (!raw) return '';
                const fromUrl = raw.match(/playlist\/(\d+)/i) || raw.match(/[?&]id=(\d+)/i);
                if (fromUrl) return fromUrl[1];
                if (/^\d{5,}$/.test(raw)) return raw;
                return '';
            },
            searchPlaylists: function () {
                const keyword = String(this.playlistKeyword || '').trim();
                if (!keyword) {
                    this.$toast && this.$toast.message('请输入歌单名称或歌单 ID');
                    return;
                }
                const id = this.extractPlaylistId(keyword);
                if (id) {
                    this.openPlaylistDetail({ id: id, name: '歌单 ' + id });
                    return;
                }
                this.playlistType = 'search';
                this.playlistView = 'list';
                this.fetchPlaylists(1, true);
            },
            loadPlaylistShortcut: function (type) {
                this.playlistType = type;
                this.playlistKeyword = '';
                this.playlistView = 'list';
                this.fetchPlaylists(1, true);
            },
            loadMorePlaylists: function () {
                if (this.playlistLoading || !this.playlistHasMore) return;
                this.fetchPlaylists((this.playlistPage || 1) + 1, false);
            },
            playlistCover: function (row) {
                if (row && row.cover) return row.cover;
                return this.searchCover(row);
            },
            fetchPlaylists: function (page, reset) {
                if (reset) {
                    this.playlistReqId += 1;
                    this.playlistPage = 1;
                    this.playlistList = [];
                    this.playlistHasMore = true;
                    this.playlistError = '';
                    page = 1;
                } else if (this.playlistLoading) {
                    return;
                } else if (!this.playlistHasMore) {
                    return;
                }
                const reqId = this.playlistReqId;
                const pageNo = page || 1;
                const pageSize = this.playlistLimit || 20;
                this.playlistLoading = true;
                const keyword = String(this.playlistKeyword || '').trim();
                const loadJson = (u) => fetch(u, { credentials: 'same-origin' }).then((res) => {
                    if (!res.ok) throw new Error('http ' + res.status);
                    return res.json();
                });
                const isOkList = (json) => json && Number(json.result) === 100 && json.data && Array.isArray(json.data.list);
                let pending;
                if (this.playlistType === 'search' && keyword) {
                    const searchUrl = '/api/qq/search?t=2'
                        + '&key=' + encodeURIComponent(keyword)
                        + '&pageNo=' + encodeURIComponent(pageNo)
                        + '&pageSize=' + encodeURIComponent(pageSize);
                    const legacyUrl = '/api/qq/playlists?q=' + encodeURIComponent(keyword)
                        + '&pageNo=' + encodeURIComponent(pageNo)
                        + '&pageSize=' + encodeURIComponent(pageSize);
                    pending = loadJson(searchUrl)
                        .then((json) => (isOkList(json) ? json : loadJson(legacyUrl)))
                        .catch(() => loadJson(legacyUrl));
                } else {
                    const url = '/api/qq/playlists?pageNo=' + encodeURIComponent(pageNo)
                        + '&pageSize=' + encodeURIComponent(pageSize)
                        + '&type=' + encodeURIComponent(this.playlistType || 'hot');
                    pending = loadJson(url);
                }
                pending.then((json) => {
                        if (reqId !== this.playlistReqId) return;
                        if (!json || Number(json.result) !== 100 || !json.data) {
                            if (pageNo <= 1) {
                                this.playlistList = [];
                                this.playlistCount = 0;
                            }
                            this.playlistHasMore = false;
                            this.playlistError = (json && json.message) ? json.message : '歌单暂不可用';
                            return;
                        }
                        const rawList = Array.isArray(json.data.list) ? json.data.list : [];
                        const list = this.uniqBySongId(rawList.map((item) => this.normalizeQqPlaylist(item)).filter(Boolean));
                        const before = pageNo <= 1 ? [] : (this.playlistList || []);
                        const merged = this.uniqBySongId(before.concat(list));
                        const added = merged.length - before.length;
                        this.playlistList = merged;
                        this.playlistPage = pageNo;
                        this.playlistCount = Number(json.data.total) || merged.length;
                        this.playlistHasMore = added >= pageSize && (!this.playlistCount || merged.length < this.playlistCount);
                    })
                    .catch(() => {
                        if (reqId !== this.playlistReqId) return;
                        if (pageNo <= 1) {
                            this.playlistList = [];
                            this.playlistCount = 0;
                        }
                        this.playlistHasMore = false;
                        this.playlistError = '歌单获取失败';
                    })
                    .then(() => {
                        if (reqId === this.playlistReqId) {
                            this.playlistLoading = false;
                        }
                    });
            },
            openPlaylistDetail: function (row) {
                const playlist = this.normalizeQqPlaylist(row);
                if (!playlist || !playlist.id) {
                    this.$toast && this.$toast.message('缺少歌单 ID');
                    return;
                }
                this.playlistDetailReqId += 1;
                const reqId = this.playlistDetailReqId;
                this.playlistView = 'detail';
                this.playlistDetail = {
                    id: playlist.id,
                    name: playlist.name || '歌单',
                    creator: playlist.creator || '',
                    songCount: playlist.songCount || 0,
                    cover: playlist.cover || ''
                };
                this.playlistSongs = [];
                this.playlistSongsAll = [];
                this.playlistSongsShown = 20;
                this.playlistSongsLoading = false;
                this.playlistLoading = true;
                this.playlistError = '';
                const detailUrl = '/api/qq/songlist?id=' + encodeURIComponent(playlist.id);
                const fallbackUrl = '/api/qq/playlist?id=' + encodeURIComponent(playlist.id);
                const loadJson = (url) => fetch(url, { credentials: 'same-origin' })
                    .then((res) => {
                        if (!res.ok) throw new Error('http ' + res.status);
                        return res.json();
                    });
                loadJson(detailUrl)
                    .then((json) => {
                        if (json && Number(json.result) === 100 && json.data) return json;
                        return loadJson(fallbackUrl);
                    })
                    .catch(() => loadJson(fallbackUrl))
                    .then((json) => {
                        if (reqId !== this.playlistDetailReqId) return;
                        if (!json || Number(json.result) !== 100 || !json.data) {
                            this.playlistError = (json && (json.message || json.errMsg)) ? (json.message || json.errMsg) : '歌单详情暂不可用';
                            this.$toast && this.$toast.error(this.playlistError);
                            return;
                        }
                        const parsed = this.normalizeQqPlaylistDetail(json.data, playlist);
                        this.playlistDetail = parsed.detail;
                        this.playlistSongsAll = this.uniqBySongId(parsed.songs);
                        this.playlistSongsShown = 20;
                        this.playlistSongs = this.playlistSongsAll.slice(0, 20);
                        if (!this.playlistSongsAll.length) {
                            this.playlistError = '这个歌单没有可播放的歌曲';
                            this.$toast && this.$toast.message(this.playlistError);
                        }
                    })
                    .catch(() => {
                        if (reqId !== this.playlistDetailReqId) return;
                        this.playlistError = '歌单详情获取失败';
                        this.$toast && this.$toast.error(this.playlistError);
                    })
                    .then(() => {
                        if (reqId === this.playlistDetailReqId) {
                            this.playlistLoading = false;
                        }
                    });
            },
            loadMorePlaylistSongs: function () {
                if (this.playlistSongsLoading) return;
                const all = this.playlistSongsAll || [];
                if (this.playlistSongs.length >= all.length) return;
                this.playlistSongsLoading = true;
                this.playlistSongsShown += 20;
                this.playlistSongs = all.slice(0, this.playlistSongsShown);
                const _this = this;
                this.$nextTick(function () {
                    _this.playlistSongsLoading = false;
                });
            },
            backToPlaylistList: function () {
                this.playlistView = 'list';
                this.playlistSongs = [];
                this.playlistSongsAll = [];
                this.playlistError = '';
            },
            normalizeNickName: function (name) {
                return String(name == null ? '' : name).trim().replace(/\s+/g, ' ');
            },
            isNickNameConflict: function (name) {
                const want = this.normalizeNickName(name).toLowerCase();
                if (!want) return false;
                const meIds = this.$store.getters.getMeSessionIds || [];
                const members = this.onlineMemberList || [];
                for (let i = 0; i < members.length; i++) {
                    const member = members[i] || {};
                    const other = this.normalizeNickName(member.nickName || member.name).toLowerCase();
                    if (!other || other !== want) continue;
                    const sid = String(member.sessionId || '').trim();
                    if (sid && meIds.indexOf(sid) !== -1) continue;
                    const me = this.normalizeNickName(this.userName).toLowerCase();
                    if (sid) return true;
                    if (other !== me) return true;
                }
                return false;
            },
            applyNickNameChange: function (raw) {
                const name = this.normalizeNickName(raw);
                if (!name) {
                    this.$toast && this.$toast.message('请输入昵称');
                    return;
                }
                if (this.isNickNameConflict(name)) {
                    this.$toast && this.$toast.error('昵称已被使用');
                    return;
                }
                this.settingName(name);
            },
            settingName: function (name) {
                let stompClient = this.$store.getters.getStompClient;
                if (!stompClient || typeof stompClient.send !== 'function') {
                    this.$toast && this.$toast.error('尚未连接服务器');
                    return;
                }
                if (!this.isContented) {
                    this.$toast && this.$toast.error('尚未连接服务器');
                    return;
                }
                stompClient.send('/setting/name', {}, JSON.stringify({
                    name: name,
                    sendTime: Date.now()
                }));
            },
            search: function () {
                let stompClient = this.$store.getters.getStompClient;
                if (!this.isContented || !stompClient || typeof stompClient.send !== 'function') {
                    this.$toast && this.$toast.error('尚未连接服务器，请先点击播放进入房间');
                    return;
                }
                const keyword = this.$store.getters.getSearchKeyword;
                if (!keyword || !String(keyword).trim()) {
                    this.$toast && this.$toast.message('请输入搜索关键字');
                    return;
                }
                if (this.searchLoading) {
                    this.searchStaleLeft = (this.searchStaleLeft || 0) + 1;
                }
                this.current = 1;
                this.searchLoadPage = 1;
                this.searchLoading = true;
                this.searchHasMore = true;
                this.armSearchTimeout();
                this.$store.commit('setSearchData', []);
                this.$store.commit('setSearchCount', 0);
                stompClient.send('/music/search', {}, JSON.stringify({
                    name: keyword,
                    sendTime: Date.now(),
                    source: 'qq',
                    pageIndex: 1,
                    pageSize: this.limit
                }));
            },
            loadMoreSearch: function () {
                if (this.searchLoading || !this.searchHasMore) return;
                const stompClient = this.$store.getters.getStompClient;
                if (!this.isContented || !stompClient || typeof stompClient.send !== 'function') return;
                this.searchLoading = true;
                this.armSearchTimeout();
                this.searchLoadPage = (this.current || 1) + 1;
                this.current = this.searchLoadPage;
                stompClient.send('/music/search', {}, JSON.stringify({
                    name: this.$store.getters.getSearchKeyword,
                    sendTime: Date.now(),
                    source: 'qq',
                    pageIndex: this.current,
                    pageSize: this.limit
                }));
            },
            /** 真实票数：后端 ips 集合大小（多人累加） */
            goodCount: function (row) {
                if (!row) return 0;
                const ips = row.ips;
                if (!ips) return 0;
                if (Array.isArray(ips)) return ips.length;
                // Jackson 偶发把 Set 序列化成对象
                if (typeof ips === 'object') return Object.keys(ips).length;
                return 0;
            },
            /** 我是否已赞（本会话；用于实心高亮与取消提示） */
            isLikedByMe: function (row) {
                if (!row || row.id == null) return false;
                return !!this.myLikedMap[String(row.id)];
            },
            loadMyLikedMap: function () {
                try {
                    const raw = window.sessionStorage.getItem('jusic_my_liked');
                    const obj = raw ? JSON.parse(raw) : {};
                    this.myLikedMap = obj && typeof obj === 'object' ? obj : {};
                } catch (e) {
                    this.myLikedMap = {};
                }
            },
            saveMyLikedMap: function () {
                try {
                    window.sessionStorage.setItem('jusic_my_liked', JSON.stringify(this.myLikedMap || {}));
                } catch (e) { /* ignore */ }
            },
            markPendingLikeResult: function (liked) {
                const id = this.pendingLikeMusicId;
                if (id == null || id === '') return;
                const key = String(id);
                if (liked) {
                    this.$set(this.myLikedMap, key, true);
                } else {
                    this.$delete(this.myLikedMap, key);
                }
                this.saveMyLikedMap();
                this.pendingLikeMusicId = null;
            },
	    goodMusic:function(row){
                if (!row || !row.id) {
                    this.$toast && this.$toast.error('歌曲信息无效');
                    return;
                }
                if (this.music && this.music.id && String(this.music.id) === String(row.id)) {
                    this.$toast && this.$toast.message('正在播放的歌曲无需点赞');
                    return;
                }
	    	let stompClient = this.$store.getters.getStompClient;
                if (!this.isContented || !stompClient || typeof stompClient.send !== 'function') {
                    this.$toast && this.$toast.error('尚未连接服务器，请先进入房间');
                    return;
                }
                this.pendingLikeMusicId = row.id;
                stompClient.send('/music/good/' + row.id, {}, {});
                // 结果 toast 由 NOTICE 带回（含歌名与票数），此处不额外刷一条
	    },
            pickMusic: function(row) {
                let stompClient = this.$store.getters.getStompClient;
                if (!this.isContented || !stompClient || typeof stompClient.send !== 'function') {
                    this.$toast && this.$toast.error('尚未连接服务器，请先进入房间');
                    return;
                }
                this.$store.commit('setPendingPickHint', row.name);
                this.pendingMyPick = {
                    name: row.name || '',
                    id: row.id != null ? String(row.id) : null,
                    ts: Date.now()
                };
                stompClient.send('/music/pick', {}, JSON.stringify({
                    name: row.name,
                    id:row.id,
		    source: 'qq',
                    sendTime: Date.now()
                }));
                this.$toast && this.$toast.message(`正在点歌：${row.name}`);
            },
            /** 从 NOTICE 文案解析《歌名》 */
            extractSongNameFromNotice: function (msg) {
                if (!msg) return '';
                const m = String(msg).match(/《([^》]+)》/);
                return m && m[1] ? m[1] : '';
            },
            /** 记录本会话我点过的歌（用于即将播放 / 正在播放提示） */
            markAsMyPicked: function (musicId) {
                if (musicId == null || musicId === '') return;
                this.$set(this.myPickedMap, String(musicId), true);
                this.$store.commit('rememberMePickedId', String(musicId));
            },
            rememberCurrentSession: function (socketClient, stompClient) {
                const sid = this.extractSockJsSessionId(socketClient || stompClient);
                if (sid) {
                    this.$store.commit('rememberMeSessionId', sid);
                }
            },
            extractSockJsSessionId: function (client) {
                try {
                    const ws = client && (client.ws || client);
                    const url = (ws && (ws._transport && ws._transport.url)) || (ws && ws.url) || '';
                    const matched = String(url).match(/\/([^/?]+)\/(?:websocket|xhr_streaming|xhr_polling|eventsource)/i);
                    if (matched && matched[1] && matched[1].length > 4) {
                        return matched[1];
                    }
                } catch (e) { /* ignore */ }
                return '';
            },
            rememberMeFromPickChat: function (data) {
                if (!data || !data.content) return;
                const pending = this.pendingMyPick || this.peekPendingMyPick();
                const hint = this.$store.getters.getPendingPickHint;
                const pickName = (pending && pending.name) || hint;
                if (!pickName) return;
                const content = String(data.content);
                if (content.indexOf('点歌') === -1 || content.indexOf(pickName) === -1) return;
                if (data.sessionId) {
                    this.$store.commit('rememberMeSessionId', data.sessionId);
                }
                if (data.nickName) {
                    this.$store.commit('rememberMeNickName', data.nickName);
                }
            },
            rememberMeFromHistory: function (list) {
                if (!Array.isArray(list) || !list.length) return;
                const pending = this.pendingMyPick || this.peekPendingMyPick();
                const hint = this.$store.getters.getPendingPickHint;
                const pickName = (pending && pending.name) || hint;
                const pickId = pending && pending.id;
                let found = null;
                if (pickId) {
                    found = list.find(item => item && String(item.musicId || item.id) === String(pickId));
                }
                if (!found && pickName) {
                    const name = String(pickName).trim();
                    found = list.find(item => item && item.name && String(item.name).trim() === name);
                }
                if (!found) return;
                if (found.pickUserId || found.sessionId) {
                    this.$store.commit('rememberMeSessionId', found.pickUserId || found.sessionId);
                }
                if (found.pickUserName || found.nickName) {
                    this.$store.commit('rememberMeNickName', found.pickUserName || found.nickName);
                }
                if (found.musicId || found.id) {
                    this.markAsMyPicked(found.musicId || found.id);
                }
            },
            isMyPicked: function (musicId) {
                if (musicId == null || musicId === '') return false;
                return !!this.myPickedMap[String(musicId)];
            },
            peekPendingMyPick: function () {
                try {
                    const raw = window.sessionStorage.getItem('jusic_pending_my_pick');
                    if (!raw) return null;
                    const obj = JSON.parse(raw);
                    if (obj && (obj.name || obj.id)) {
                        return {
                            name: obj.name || '',
                            id: obj.id != null ? String(obj.id) : null,
                            ts: obj.ts || Date.now()
                        };
                    }
                } catch (e) { /* ignore */ }
                return null;
            },
            /** 历史面板点歌时写入的 pending（跨组件） */
            pullPendingMyPickFromStorage: function () {
                try {
                    const raw = window.sessionStorage.getItem('jusic_pending_my_pick');
                    if (!raw) return;
                    window.sessionStorage.removeItem('jusic_pending_my_pick');
                    const obj = JSON.parse(raw);
                    if (obj && (obj.name || obj.id)) {
                        this.pendingMyPick = {
                            name: obj.name || '',
                            id: obj.id != null ? String(obj.id) : null,
                            ts: obj.ts || Date.now()
                        };
                    }
                } catch (e) { /* ignore */ }
            },
            /**
             * 点歌成功后，用 pendingMyPick 从 PICK 列表认领真实 musicId
             *（聊天点歌只有歌名；搜索点歌可能已有 id）
             */
            claimMyPickedFromList: function (list) {
                this.pullPendingMyPickFromStorage();
                if (!this.pendingMyPick || !Array.isArray(list) || !list.length) return;
                const pending = this.pendingMyPick;
                let found = null;
                if (pending.id) {
                    found = list.find(m => m && String(m.id) === String(pending.id));
                }
                if (!found && pending.name) {
                    // 从队尾往前找同名且 pickTime 接近的（更可能是刚点的）
                    const name = String(pending.name).trim();
                    const candidates = list.filter(m => m && m.name && String(m.name).trim() === name);
                    if (candidates.length === 1) {
                        found = candidates[0];
                    } else if (candidates.length > 1) {
                        candidates.sort((a, b) => Number(b.pickTime || 0) - Number(a.pickTime || 0));
                        found = candidates[0];
                    }
                }
                if (found && found.id != null) {
                    this.markAsMyPicked(found.id);
                    if (found.sessionId) {
                        this.$store.commit('rememberMeSessionId', found.sessionId);
                    }
                    if (found.nickName) {
                        this.$store.commit('rememberMeNickName', found.nickName);
                    }
                    this.pendingMyPick = null;
                } else if (pending.id) {
                    // 列表里暂时没有也先记下 id
                    this.markAsMyPicked(pending.id);
                    this.pendingMyPick = null;
                } else if (pending.ts && Date.now() - pending.ts > 15000) {
                    // 超时放弃，避免一直挂着
                    this.pendingMyPick = null;
                }
            },
            /** 下一首是我点的 → 轻提示一次 */
            maybeHintMySongUpcoming: function (list) {
                if (!Array.isArray(list) || list.length < 2) return;
                // pick[0] 正在播，pick[1] 下一首
                const next = list[1];
                if (!next || next.id == null) return;
                if (!this.isMyPicked(next.id)) return;
                const key = 'upcoming:' + String(next.id);
                if (this.mySongHintShown[key]) return;
                this.$set(this.mySongHintShown, key, true);
                const name = next.name || '未知歌曲';
                this.$toast && this.$toast.message(`你点的《${name}》即将播放`);
            },
            /** 开始播放我点的歌 → 轻提示一次 */
            maybeHintMySongPlaying: function (musicPayload) {
                if (!musicPayload || musicPayload.id == null) return;
                if (!this.isMyPicked(musicPayload.id)) return;
                const key = 'playing:' + String(musicPayload.id);
                if (this.mySongHintShown[key]) return;
                this.$set(this.mySongHintShown, key, true);
                const name = musicPayload.name || '未知歌曲';
                this.$toast && this.$toast.message(`正在播放你点的《${name}》`);
            },
            /** 构建分享文案（生产域名可配置，禁止写死 localhost） */
            buildShareText: function () {
                const link = shareUrl || 'https://311music.xyz';
                const m = this.music;
                const hasSong = m && m.name && String(m.name).trim()
                    && m.name !== '暂无可用音乐';
                if (hasSong) {
                    const artist = m.artist ? String(m.artist).trim() : '';
                    const songPart = artist
                        ? `正在听《${m.name}》- ${artist}`
                        : `正在听《${m.name}》`;
                    // 例：正在听《志明与春娇》- 五月天 一起听歌吧: https://311music.xyz
                    return `${songPart} 一起听歌吧: ${link}`;
                }
                return `一起听歌吧: ${link}`;
            },
            /** 一键分享：写入剪贴板 + 失败降级 */
            shareRoom: async function () {
                const text = this.buildShareText();
                this.shareFallbackText = text;
                const ok = await this.copyTextToClipboard(text);
                if (ok) {
                    this.$toast && this.$toast.success('已复制，去群里粘贴吧');
                    this.openShareFallback = false;
                } else {
                    this.openShareFallback = true;
                    this.$toast && this.$toast.error('复制失败，请长按下方文案手动复制');
                }
            },
            retryCopyShare: async function () {
                const text = this.shareFallbackText || this.buildShareText();
                const ok = await this.copyTextToClipboard(text);
                if (ok) {
                    this.$toast && this.$toast.success('已复制，去群里粘贴吧');
                    this.openShareFallback = false;
                } else {
                    this.$toast && this.$toast.error('仍无法复制，请长按文案手动复制');
                }
            },
            /**
             * 剪贴板写入：优先 Clipboard API，失败则 execCommand，再失败返回 false
             * （非 HTTPS / 权限拒绝时常见）
             */
            copyTextToClipboard: async function (text) {
                if (!text) return false;
                try {
                    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                        await navigator.clipboard.writeText(text);
                        return true;
                    }
                } catch (e) {
                    // fall through
                }
                try {
                    const ta = document.createElement('textarea');
                    ta.value = text;
                    ta.setAttribute('readonly', '');
                    ta.style.position = 'fixed';
                    ta.style.top = '0';
                    ta.style.left = '0';
                    ta.style.width = '1px';
                    ta.style.height = '1px';
                    ta.style.padding = '0';
                    ta.style.border = 'none';
                    ta.style.outline = 'none';
                    ta.style.boxShadow = 'none';
                    ta.style.background = 'transparent';
                    ta.style.opacity = '0';
                    document.body.appendChild(ta);
                    ta.focus();
                    ta.select();
                    ta.setSelectionRange(0, ta.value.length);
                    const ok = document.execCommand('copy');
                    document.body.removeChild(ta);
                    return !!ok;
                } catch (e2) {
                    return false;
                }
            },
            submitCookie: function () {
                const raw = (this.cookieInput || '').trim();
                if (!raw) {
                    this.$toast && this.$toast.message('请先粘贴 Cookie');
                    return;
                }
                if (!this.isRoot && !this.isAdmin) {
                    this.$toast && this.$toast.error('请先登录管理员：聊天发送 root 你的密码');
                    return;
                }
                let stompClient = this.$store.getters.getStompClient;
                if (!this.isContented || !stompClient || typeof stompClient.send !== 'function') {
                    this.$toast && this.$toast.error('尚未连接服务器');
                    return;
                }
                this.cookieSubmitting = true;
                stompClient.send('/admin/cookie', {}, JSON.stringify({
                    content: raw,
                    sendTime: Date.now()
                }));
                this.$toast && this.$toast.message('已提交 Cookie，请看聊天区成功/失败提示');
                // 等 NOTICE 回来后再关弹窗；先保留输入方便失败重试
                setTimeout(() => {
                    this.cookieSubmitting = false;
                }, 1200);
            },
            adminSessionId: function () {
                const ids = this.$store.getters.getMeSessionIds || [];
                return ids.length ? ids[ids.length - 1] : '';
            },
            adminFetch: function (path) {
                const sid = this.adminSessionId();
                const join = path.indexOf('?') >= 0 ? '&' : '?';
                return fetch(path + join + 'session=' + encodeURIComponent(sid), { credentials: 'same-origin' })
                    .then(function (res) { return res.json(); })
                    .catch(function () { return { result: 400, errMsg: '请求失败' }; });
            },
            loadCookieStatus: function () {
                const _this = this;
                if (!(this.isRoot || this.isAdmin)) return;
                this.adminFetch('/api/admin/qq-cookie-status').then(function (json) {
                    const data = (json && json.data) || {};
                    const ok = !!data.configured;
                    _this.cookieStatusOk = ok;
                    if (ok) {
                        _this.cookieStatusText = '已配置' + (data.uin ? '（uin ' + data.uin + '）' : '');
                        _this.cookieChannelHint = '';
                    } else {
                        _this.cookieStatusText = '未配置有效 Cookie，播放通道可能失效';
                        _this.cookieChannelHint = '通道失效';
                    }
                });
            },
            showPickButton(value) {
                if (!value || typeof value !== 'object') {
                    // QQ 等源可能不返回 privilege，默认允许点歌
                    return true;
                }
                if (Number(value.st) < 0) {
                    // 没有资源
                    return false;
                } else if (Number(value.fl) === 0) {
                    // 可能需要付费
                    return false;
                }
                return true
            },
            musicSkipVote: function () {
                let stompClient = this.$store.getters.getStompClient;
                if (!this.isContented || !stompClient || typeof stompClient.send !== 'function') {
                    this.$toast && this.$toast.error('尚未连接服务器');
                    return;
                }
                stompClient.send('/music/skip/vote', {}, {});
            },
            musicTimeUpdate: function (e) {
                // progress
                let currentTime = e.target.currentTime;
                let duration = e.target.duration;
                if (typeof currentTime === 'number' && isFinite(currentTime)
                    && Math.abs(currentTime - (this.lastAudioTime || 0)) > 0.05) {
                    this.clearStuckWatch();
                    this.lastAudioTime = currentTime;
                    this.lastAudioTimeAt = Date.now();
                }
                this.$store.commit('setPlayerProgress', (currentTime / duration) * 100);

                // show time
                let usedTimeHH_mm_ss = timeUtils.secondsToHH_mm_ss(currentTime);
                let durationHH_mm_ss = timeUtils.secondsToHH_mm_ss(duration);
                let time = usedTimeHH_mm_ss + ' / ' + durationHH_mm_ss;
                this.$store.commit('setPlayerTime', time);

                this.syncActiveLyric(currentTime, duration);
            },
            resolveActiveLyricIndex: function (currentTime, duration) {
                const lines = this.lyricLines;
                if (!lines || !lines.length) {
                    return -1;
                }
                const t = Number(currentTime);
                if (!isFinite(t) || t < 0) {
                    return -1;
                }
                const last = lines.length - 1;
                const lastTime = Number(lines[last].time) || 0;
                const dur = Number(duration);
                const hasDuration = isFinite(dur) && dur > 0;
                const nearEnd = hasDuration && t >= Math.max(0, dur - 0.6);
                const lastAfterDuration = hasDuration && lastTime > dur + 0.2;
                if (nearEnd || (lastAfterDuration && t >= Math.max(0, dur - 1))) {
                    return last;
                }
                let idx = -1;
                for (let i = 0; i < lines.length; i++) {
                    if (Number(lines[i].time) <= t + 0.05) {
                        idx = i;
                    } else {
                        break;
                    }
                }
                return idx;
            },
            syncActiveLyric: function (currentTime, duration) {
                const lines = this.lyricLines;
                if (!lines || !lines.length) {
                    if (this.activeLyricIndex !== -1) {
                        this.activeLyricIndex = -1;
                    }
                    this.$store.commit('setPlayerLyric', '暂无歌词');
                    return;
                }
                const idx = this.resolveActiveLyricIndex(currentTime, duration);
                if (idx !== this.activeLyricIndex) {
                    this.activeLyricIndex = idx;
                }
                if (idx >= 0 && lines[idx]) {
                    this.$store.commit('setPlayerLyric', lines[idx].text);
                }
            },
            getScreenWidth: function () {
                const _this = this;
                window.onresize = () => {
                    return (() => {
                        _this.screenWidth = document.documentElement.clientWidth;
                    })()
                };
            },
            formatterTime: function(value) {
                return timeUtils.secondsToHH_mm_ss(value)
            },
	    nextSong:function(e){
		this.firstLoaded=1;
		this.$store.commit('setMusic2', {"url":this.secondUrl});
		//document.querySelector('#music2').src = this.secondUrl;

		//console.log(this.secondUrl);
		//console.log("第二首");
	    },
            logPlayback: function (event, extra) {
                const now = Date.now();
                const key = String(event) + '|' + String(extra || '');
                if (this.lastPlayLogKey === key && now - this.lastPlayLogAt < 2500) {
                    return;
                }
                this.lastPlayLogKey = key;
                this.lastPlayLogAt = now;
                const m = this.music || {};
                console.info('[play]', event, m.name || '', extra || '');
            },
            calcServerSeekSec: function (music) {
                if (!music || !music.pushTime) {
                    return 0;
                }
                let sec = (Date.now() - Number(music.pushTime)) / 1000;
                if (!isFinite(sec) || sec < 0) {
                    sec = 0;
                }
                if (music.duration) {
                    const max = Number(music.duration) / 1000 - 0.3;
                    if (max > 0 && sec > max) {
                        sec = Math.max(0, max);
                    }
                }
                return sec;
            },
            withCacheBust: function (url) {
                if (!url) {
                    return url;
                }
                const cleaned = String(url).replace(/[?&]_r=\d+/g, '').replace(/\?&/, '?').replace(/\?$/, '');
                const sep = cleaned.indexOf('?') >= 0 ? '&' : '?';
                return cleaned + sep + '_r=' + Date.now();
            },
            isStreamProxyUrl: function (url) {
                return !!url && /\/api\/qq\/stream(\?|$)/.test(String(url));
            },
            resolvePlaybackUrl: function (raw) {
                if (!raw) {
                    return '';
                }
                const url = String(raw).trim();
                if (!url) {
                    return '';
                }
                const stream = this.isStreamProxyUrl(url);
                // 代理播放必须同域相对路径：禁止拼 127.0.0.1，否则手机/舍友电脑连不到
                if (stream) {
                    const qIndex = url.indexOf('?');
                    const query = qIndex >= 0 ? url.slice(qIndex) : '';
                    return '/api/qq/stream' + query;
                }
                if (/^https?:\/\//i.test(url)) {
                    if (/^https?:\/\/(127\.0\.0\.1|localhost)(:\d+)?\//i.test(url)) {
                        try {
                            const parsed = new URL(url);
                            return parsed.pathname + parsed.search;
                        } catch (e) {
                            return url;
                        }
                    }
                    return url;
                }
                if (url.charAt(0) === '/') {
                    return url;
                }
                return url;
            },
            getAudioEl: function () {
                return document.querySelector('#music');
            },
            clearStuckWatch: function () {
                if (this.stuckTimer) {
                    clearTimeout(this.stuckTimer);
                    this.stuckTimer = null;
                }
            },
            giveUpPlayback: function (msg) {
                if (this.recoverGaveUp) {
                    return;
                }
                this.recoverGaveUp = true;
                this.audioRecovering = false;
                this.clearStuckWatch();
                this.stopPlaybackWatchdog();
                this.audioSrcOverride = '';
                this.suppressStuckUntil = Date.now() + 8000;
                const audio = this.getAudioEl();
                if (audio) {
                    try { audio.pause(); } catch (e) { /* ignore */ }
                    try {
                        audio.removeAttribute('src');
                        audio.load();
                    } catch (e) { /* ignore */ }
                }
                this.logPlayback('give-up', msg || 'url-failed');
            },
            tryPlaybackFallback: function () {
                if (this.playbackFallbackTried || this.recoverGaveUp) {
                    return false;
                }
                const music = this.music;
                if (!music || !music.id || music.id === 'placeholder') {
                    return false;
                }
                const raw = String(music.url || '');
                const current = String(this.audioSrcOverride || raw);
                const usedStream = this.isStreamProxyUrl(raw) || this.isStreamProxyUrl(current);
                this.playbackFallbackTried = true;
                let next = '';
                if (!usedStream && music.id) {
                    next = this.resolvePlaybackUrl('/api/qq/stream?id=' + encodeURIComponent(music.id));
                } else if (usedStream && /^https?:\/\//i.test(raw) && !this.isStreamProxyUrl(raw)) {
                    next = raw;
                }
                if (!next) {
                    return false;
                }
                this.logPlayback('fallback', next);
                this.audioRecovering = true;
                this.suppressStuckUntil = Date.now() + 8000;
                this.audioSrcOverride = this.withCacheBust(next);
                const _this = this;
                this.$nextTick(function () {
                    const audio = _this.getAudioEl();
                    if (!audio) {
                        _this.audioRecovering = false;
                        return;
                    }
                    try { audio.load(); } catch (e) { /* ignore */ }
                    _this.tryPlay(audio);
                    _this.audioRecovering = false;
                });
                return true;
            },
            onAudioMediaError: function () {
                if (this.recoverGaveUp) {
                    return;
                }
                const audio = this.getAudioEl();
                const code = audio && audio.error ? audio.error.code : 0;
                this.logPlayback('media-error', 'code=' + code);
                if (code === 2 || code === 4) {
                    if (this.tryPlaybackFallback()) {
                        return;
                    }
                    this.giveUpPlayback('播放地址无效，已停止重试');
                    return;
                }
                this.onAudioStuck('error');
            },
            onAudioStuck: function (reason) {
                if (this.recoverGaveUp || !this.isPlay || this.audioRecovering) {
                    return;
                }
                if (this.suppressStuckUntil && Date.now() < this.suppressStuckUntil) {
                    return;
                }
                const music = this.music;
                if (!music || !music.url || music.id === 'placeholder') {
                    return;
                }
                if (this.stuckTimer) {
                    return;
                }
                const waitMs = reason === 'error' ? 800 : 4000;
                const _this = this;
                this.stuckTimer = setTimeout(function () {
                    _this.stuckTimer = null;
                    _this.recoverPlayback(reason);
                }, waitMs);
            },
            onAudioPause: function () {
                if (this.userPaused) {
                    this.isAudioPlaying = false;
                    return;
                }
                if (!this.isPlay || this.audioRecovering) {
                    return;
                }
                if (this.suppressStuckUntil && Date.now() < this.suppressStuckUntil) {
                    return;
                }
                if (typeof document !== 'undefined' && document.hidden) {
                    this.pausedWhileHidden = true;
                    return;
                }
                this.onAudioStuck('pause');
            },
            onAudioPlaying: function () {
                this.pausedWhileHidden = false;
                this.userPaused = false;
                this.isAudioPlaying = true;
                this.lastAudioTimeAt = Date.now();
                this.clearStuckWatch();
            },
            onAudioEnded: function () {
                const audio = this.getAudioEl();
                const currentTime = audio && isFinite(audio.currentTime) ? audio.currentTime : 0;
                const duration = audio && isFinite(audio.duration) ? audio.duration : (this.music && this.music.duration ? Number(this.music.duration) / 1000 : currentTime);
                this.syncActiveLyric(Math.max(currentTime, duration), duration);
                const music = this.music;
                if (!music || !music.duration) {
                    return;
                }
                const seekSec = this.calcServerSeekSec(music);
                const remain = Number(music.duration) / 1000 - seekSec;
                if (remain > 5) {
                    this.onAudioStuck('ended-early');
                }
            },
            onVisibilityChange: function () {
                if (typeof document === 'undefined' || document.hidden || !this.isPlay) {
                    return;
                }
                const audio = this.getAudioEl();
                if ((this.pausedWhileHidden || (audio && audio.paused)) && this.music && this.music.url) {
                    this.pausedWhileHidden = false;
                    this.onAudioStuck('visible-paused');
                }
            },
            recoverPlayback: function (reason) {
                if (this.recoverGaveUp) {
                    return;
                }
                const music = this.music;
                if (!music || !music.url || music.id === 'placeholder') {
                    return;
                }
                if (this.audioRecovering) {
                    return;
                }
                const songKey = String(music.id != null ? music.id : music.url);
                if (this.recoverSongId !== songKey) {
                    this.recoverSongId = songKey;
                    this.recoverCount = 0;
                    this.recoverGaveUp = false;
                    this.playbackFallbackTried = false;
                }
                this.recoverCount += 1;
                if (this.recoverCount > 3) {
                    if (this.tryPlaybackFallback()) {
                        return;
                    }
                    this.giveUpPlayback('播放中断，已停止重试');
                    return;
                }
                const playUrl = this.resolvePlaybackUrl(music.url);
                if (!playUrl) {
                    this.giveUpPlayback('播放地址无效，已停止重试');
                    return;
                }
                const seekSec = this.calcServerSeekSec(music);
                this.logPlayback(reason, 'recover #' + this.recoverCount + ' refetch-url=1 seek=' + seekSec.toFixed(1) + 's');
                this.audioRecovering = true;
                this.suppressStuckUntil = Date.now() + 8000;
                this.audioSrcOverride = this.withCacheBust(playUrl);
                const _this = this;
                this.$nextTick(function () {
                    const audio = _this.getAudioEl();
                    if (!audio) {
                        _this.audioRecovering = false;
                        return;
                    }
                    let applied = false;
                    const applySeekAndPlay = function () {
                        if (applied) {
                            return;
                        }
                        applied = true;
                        audio.removeEventListener('loadedmetadata', applySeekAndPlay);
                        audio.removeEventListener('canplay', applySeekAndPlay);
                        _this.seekAudio(audio, seekSec);
                        _this.tryPlay(audio);
                        _this.audioRecovering = false;
                    };
                    audio.addEventListener('loadedmetadata', applySeekAndPlay);
                    audio.addEventListener('canplay', applySeekAndPlay);
                    try {
                        audio.load();
                    } catch (e) { /* ignore */ }
                    setTimeout(function () {
                        if (_this.audioRecovering) {
                            applySeekAndPlay();
                        }
                    }, 1500);
                });
                if (this.recoverCount === 1 || reason === 'ws-reconnected') {
                    this.requestMusicSync();
                }
            },
            seekAudio: function (audio, seekSec) {
                if (!audio || typeof seekSec !== 'number' || !isFinite(seekSec) || seekSec <= 0) {
                    return;
                }
                try {
                    audio.currentTime = seekSec;
                } catch (e) { /* ignore not-ready */ }
            },
            tryPlay: function (audio) {
                if (!audio) {
                    return;
                }
                const _this = this;
                try {
                    const p = audio.play();
                    if (p && typeof p.catch === 'function') {
                        p.catch(function (err) {
                            _this.logPlayback('play-reject', err && err.message);
                        });
                    }
                } catch (e) {
                    this.logPlayback('play-throw', e && e.message);
                }
            },
            alignAudioToServer: function (music, options) {
                options = options || {};
                const audio = this.getAudioEl();
                if (!audio || !music) {
                    return;
                }
                const seekSec = this.calcServerSeekSec(music);
                if (options.reload && music.url) {
                    this.recoverPlayback(options.reason || 'align-reload');
                    return;
                }
                this.seekAudio(audio, seekSec);
                this.tryPlay(audio);
                if (options.reason === 'ws-reconnected' || options.reason === 'music-repush') {
                    this.logPlayback(options.reason, 'seek=' + seekSec.toFixed(1) + 's reload=0');
                }
            },
            requestMusicSync: function () {
                const now = Date.now();
                if (this.lastSyncAt && now - this.lastSyncAt < 3000) {
                    return;
                }
                const stompClient = this.$store.getters.getStompClient;
                if (!this.isContented || !stompClient || typeof stompClient.send !== 'function') {
                    return;
                }
                this.lastSyncAt = now;
                try {
                    stompClient.send('/music/sync', {}, JSON.stringify({ sendTime: now }));
                    this.logPlayback('sync', 'pull current');
                } catch (e) { /* 旧后端无此端点时忽略 */ }
            },
            afterWsConnected: function (wasReconnect) {
                if (!wasReconnect) {
                    return;
                }
                const _this = this;
                if (this.wsVerifyTimer) {
                    clearTimeout(this.wsVerifyTimer);
                }
                this.wsVerifyTimer = setTimeout(function () {
                    _this.wsVerifyTimer = null;
                    if (!_this.isContented || !_this.isPlay) {
                        return;
                    }
                    const music = _this.music;
                    if (!music || !music.url) {
                        _this.requestMusicSync();
                        return;
                    }
                    const audio = _this.getAudioEl();
                    const needReload = audio && (audio.paused || audio.readyState < 2 || audio.error);
                    _this.alignAudioToServer(music, {
                        reason: 'ws-reconnected',
                        reload: !!needReload
                    });
                    _this.requestMusicSync();
                }, 1500);
            },
            scheduleWsReconnect: function () {
                if (this.wsManualClose || !this.isPlay || this.wsReconnectTimer || this.wsConnecting) {
                    return;
                }
                this.wsReconnectAttempts = (this.wsReconnectAttempts || 0) + 1;
                const delay = Math.min(15000, 1000 * Math.pow(2, Math.min(this.wsReconnectAttempts - 1, 4)));
                if (this.wsReconnectAttempts === 1) {
                    this.$toast && this.$toast.message('连接断开，正在重连…');
                }
                this.logPlayback('ws-close', 'reconnect in ' + delay + 'ms #' + this.wsReconnectAttempts);
                const _this = this;
                this.wsReconnectTimer = setTimeout(function () {
                    _this.wsReconnectTimer = null;
                    if (_this.isPlay && !_this.wsManualClose && !_this.isContented) {
                        _this.connect();
                    }
                }, delay);
            },
            clearWsReconnectTimer: function () {
                if (this.wsReconnectTimer) {
                    clearTimeout(this.wsReconnectTimer);
                    this.wsReconnectTimer = null;
                }
                if (this.wsVerifyTimer) {
                    clearTimeout(this.wsVerifyTimer);
                    this.wsVerifyTimer = null;
                }
            },
            startPlaybackWatchdog: function () {
                if (this.watchdogTimer) {
                    return;
                }
                const _this = this;
                this.watchdogTimer = setInterval(function () {
                    if (!_this.isPlay || _this.audioRecovering || _this.recoverGaveUp) {
                        return;
                    }
                    if (typeof document !== 'undefined' && document.hidden) {
                        return;
                    }
                    if (_this.suppressStuckUntil && Date.now() < _this.suppressStuckUntil) {
                        return;
                    }
                    const audio = _this.getAudioEl();
                    const music = _this.music;
                    if (!audio || !music || !music.url || music.id === 'placeholder') {
                        return;
                    }
                    if (!_this.lastAudioTimeAt) {
                        return;
                    }
                    const silentFor = Date.now() - _this.lastAudioTimeAt;
                    if (silentFor > 5000) {
                        _this.onAudioStuck(audio.paused ? 'watchdog-paused' : 'watchdog');
                    }
                }, 2000);
                if (typeof document !== 'undefined') {
                    document.addEventListener('visibilitychange', this.onVisibilityChange);
                }
                if (typeof window !== 'undefined') {
                    window.__jusicSimulateStuck = function (kind) {
                        _this.clearStuckWatch();
                        _this.suppressStuckUntil = 0;
                        _this.recoverPlayback(kind || 'error');
                    };
                }
            },
            stopPlaybackWatchdog: function () {
                if (this.watchdogTimer) {
                    clearInterval(this.watchdogTimer);
                    this.watchdogTimer = null;
                }
                this.clearStuckWatch();
                if (typeof document !== 'undefined') {
                    document.removeEventListener('visibilitychange', this.onVisibilityChange);
                }
                if (typeof window !== 'undefined' && window.__jusicSimulateStuck) {
                    try {
                        delete window.__jusicSimulateStuck;
                    } catch (e) {
                        window.__jusicSimulateStuck = undefined;
                    }
                }
            }
        },
        watch: {
            isPlay: function (val) {
                if (val) {
                    this.$nextTick(() => {
                        this.bindMobileTabLayout();
                        this.scheduleMobileTabLayout();
                    });
                }
            },
            compactMode: function () {
                this.$nextTick(() => {
                    this.syncMobileTabBodyHeight();
                });
            },
            pick: function (newVal, oldVal) {
                const nextLen = (newVal && newVal.length) || 0;
                const prevLen = (oldVal && oldVal.length) || 0;
                if (nextLen <= prevLen) return;
                this.$nextTick(() => {
                    this.maybeScrollQueueToBottom(false);
                });
            },
            '$store.state.player.music': function (newValue, oldValue) {
                let _this = this;
                const sameId = oldValue && newValue && String(oldValue.id) === String(newValue.id);
                if (!sameId) {
                    this.audioSrcOverride = '';
                    this.recoverCount = 0;
                    this.recoverSongId = newValue && newValue.id != null ? String(newValue.id) : '';
                    this.recoverGaveUp = false;
                    this.playbackFallbackTried = false;
                    this.suppressStuckUntil = Date.now() + 8000;
                    this.clearStuckWatch();
                    this.lastAudioTime = 0;
                    this.lastAudioTimeAt = Date.now();
                    if (this.isPlay) {
                        this.startPlaybackWatchdog();
                    }
                } else {
                    this.suppressStuckUntil = Math.max(this.suppressStuckUntil || 0, Date.now() + 3000);
                    if (newValue && newValue.url && this.recoverGaveUp) {
                        this.recoverGaveUp = false;
                    }
                }
                this.albumRotate = false;
                this.userPaused = false;
                this.isAudioPlaying = true;
                const audioEl = document.querySelector('#music');
                if (audioEl) {
                    audioEl.volume = Number(this.$store.getters.getPlayerVolume) / 100;
                }
                if (!newValue || !newValue.url || newValue.id === 'placeholder') {
                    this.audioSrcOverride = '';
                    this.audioRecovering = false;
                    this.stopPlaybackWatchdog();
                    if (newValue && newValue.id !== 'placeholder' && !newValue.url) {
                        this.recoverGaveUp = true;
                        this.logPlayback('give-up', '当前歌曲暂无播放地址');
                    }
                    return;
                }
                // 解决部分移动端不能自动播放
                document.addEventListener('touchstart', function () {
                    const el = document.querySelector('#music');
                    if (el) {
                        el.play();
                    }
                });
                setTimeout(function () {
                    _this.albumRotate = true;
                    _this.alignAudioToServer(newValue, {
                        reason: sameId ? 'music-repush' : 'music-switch',
                        reload: false
                    });
                }, 1000)
                this.$nextTick(() => {
                    this.scheduleMobileTabLayout();
                });
            },
            activeLyricIndex: function () {
                if (this.mainTab !== 'lyrics') return;
                this.$nextTick(() => {
                    this.scrollActiveLyricIntoView();
                });
            },
            '$store.state.chat.data': function () {
                const _this = this;
                this.$nextTick(function () {
                    setTimeout(function () {
                        _this.maybeScrollChatToBottom(false);
                    }, 50);
                });
            },
            openCookie: function (val) {
                if (val) {
                    this.loadCookieStatus();
                }
            },
            screenWidth(val) {
                this.$nextTick(() => {
                    this.syncMobileTabBodyHeight();
                });
                //监控浏览器高度变化
                if (!this.timer) {
                    this.screenWidth = val;
                    this.timer = true;
                    let _this = this;
                    setTimeout(function () {
                        _this.timer = false
                    }, 400)
                }
                if (val <= 400) {
                    this.albumRotateStyle = 'border:60px solid rgb(12, 12, 12); padding: 8px;';
                    this.pageCount = 5
                }
                if (val > 400 && val <= 766) {
                    this.albumRotateSize = 450;
                    this.albumRotateStyle = 'border:70px solid rgb(12, 12, 12); padding: 8px;';
                    this.pageCount = 5
                }
                if (val > 766 && val < 1000) {
                    this.albumRotateSize = 160;
                    this.albumRotateStyle = 'border:32px solid rgb(12, 12, 12); padding: 4px;';
                }
                if (val >= 1000) {
                    this.albumRotateSize = 200;
                    this.albumRotateStyle = 'border:40px solid rgb(12, 12, 12); padding: 4px;';
                }
            }
        },
        mounted() {
            this.getScreenWidth()
            window.addEventListener('keydown', this.onFocusHotkey)
            document.addEventListener('mousedown', this.onDocumentPointer)
            document.addEventListener('touchstart', this.onDocumentPointer, { passive: true })
            document.addEventListener('visibilitychange', this.onReactionVisibilityChange)
            this.bindMobileTabLayout()
        },
        beforeDestroy() {
            this.unbindMobileTabLayout()
            window.removeEventListener('keydown', this.onFocusHotkey)
            document.removeEventListener('mousedown', this.onDocumentPointer)
            document.removeEventListener('touchstart', this.onDocumentPointer)
            document.removeEventListener('visibilitychange', this.onReactionVisibilityChange)
            this.stopPlaybackWatchdog();
            this.clearWsReconnectTimer();
            if (this.favoritePlayAllTimer) {
                clearTimeout(this.favoritePlayAllTimer);
                this.favoritePlayAllTimer = null;
            }
            if (this.reactionLockTimer) {
                clearTimeout(this.reactionLockTimer);
                this.reactionLockTimer = null;
            }
            if (this.reactionBurstTimer) {
                clearTimeout(this.reactionBurstTimer);
                this.reactionBurstTimer = null;
            }
            if (this.reactionReplayTimer) {
                clearTimeout(this.reactionReplayTimer);
                this.reactionReplayTimer = null;
            }
        },
        created() {
            this.applyMobileViewportHeight();
            // let val = this.albumRotateSize;
            let val = document.documentElement.clientWidth;
            // console.log(val);

            this.loadMyLikedMap();
            this.reloadFavorites();
            if (val <= 400) {
                this.pageCount = 5
            }
            if (val > 400 && val <= 700) {
                this.albumRotateSize = val - 60;
                this.albumRotateStyle = `border:${Math.floor(val / 10) + 10}px solid rgb(12, 12, 12);`;
                this.pageCount = 5
            }
            if (val > 700 && val <= 766) {
                this.albumRotateSize = 450;
                this.albumRotateStyle = 'border:70px solid rgb(12, 12, 12);';
            }
            if (val > 766 && val < 1000) {
                this.albumRotateSize = 160;
                this.albumRotateStyle = 'border:32px solid rgb(12, 12, 12);';
            }
            if (val >= 1000) {
                this.albumRotateSize = 200;
                this.albumRotateStyle = 'border:40px solid rgb(12, 12, 12);';
            }
            if (val > 0 && val <= 1100) {
                this.showChat = false;
            }
        }
    }
</script>

<style lang="scss" scoped>
    .cookie-admin {
        padding: 8px 4px 12px;
        max-width: 520px;
    }

    .cookie-admin-title {
        font-size: 18px;
        margin-bottom: 8px;
        color: #333;
    }

    .cookie-status-line {
        font-size: 13px;
        margin-bottom: 12px;
        color: #888;
    }

    .cookie-status-line.is-ok {
        color: #2e8b57;
    }

    .cookie-status-line.is-bad {
        color: #c0392b;
    }

    .share-fallback {
        padding: 8px 4px 12px;
        max-width: 420px;
    }

    .share-fallback-title {
        font-size: 16px;
        margin-bottom: 10px;
        line-height: 1.4;
        color: #333;
    }

    .share-fallback-input {
        user-select: all;
        -webkit-user-select: all;
    }

    .cookie-admin-tip {
        font-size: 13px;
        color: #777;
        line-height: 1.5;
        margin-bottom: 8px;
    }

    .cookie-admin code {
        color: #e74c3c;
        word-break: break-all;
    }

    .music-good-icon {
        font-size: 16px;
        line-height: 1;
    }

    .music-good-count {
        margin-left: 2px;
        font-size: 12px;
        min-width: 10px;
        font-variant-numeric: tabular-nums;
        font-weight: 500;
    }
</style>


